import { Injectable } from '@angular/core';
import { OrderAmountTypeCode, OrderProductCategory, OrderSalesStatusCode, OrderStateCode, VoucherProductGroupCode } from 'src/app/core/constants/order-constants';
import { OrderAmountModel, OrderDataModel, OrderJsonModel, OrderPartnerModel, OrderPartnerNameModel, OrderProductModel } from 'src/app/core/models/order-model/order-json';
import { OrderLedgersModel, OrderPaymentModel } from 'src/app/core/models/order-model/order-json/order-payment.model';
import { DateConverterService } from 'src/app/core/utils/date-converter.service';
import { ProductNumberType } from '../../shopping/shared/constants/shopping-constants';
import { FinanceAccountionType, OrderDetailFinanceAccountingView } from './views/order-detail-finance-accounting.view';
import { AgeGroupCodeFull, PaymentConstant, PriceRuleTypeNames, ProductCategoryConstant, ProductGroupCodeConstant } from './order.constants';
import { cloneDeep } from 'lodash';

@Injectable()
export class OrderIndividualFinanceMapperService {
    private readonly paymentCode = 'PAYMENT';
    private readonly prefixPriceDetailId = 'PRICE_DETAIL_';
    private readonly prefixExchangeId = 'EXCHANGE_';
    private readonly prefixCancelId = 'CANCEL_';
    private readonly prefixBlankDividerId = 'BLANK_';

    private readonly airIcon = 'flightsmode';
    private readonly seatIcon = 'airline_seat_recline_extra';
    private readonly mealAndBeverageIcon = 'restaurant_menu';
    private readonly bagIcon = 'luggage';
    private readonly wheelchairIcon = 'accessible';
    private readonly disabledIcon = 'escalator_warning';
    private readonly petIcon = 'pets';
    private readonly medicalIcon = 'medical_services';
    private readonly comfortIcon = 'add_reaction';
    private readonly otherIcon = 'dynamic_feed';
    private readonly currencyIcon = 'monetization_on';
    private readonly deleteIcon = 'delete';
    private readonly addShoppingCartIcon = 'add_shopping_cart';
    private readonly exchangeIcon = 'currency_exchange'
    private readonly iconClassRed = 'color-danger-500';
    private readonly discountIcon = 'percent';
    private readonly giftIcon = 'redeem';
    private readonly promotionIcon = 'sell';
    private readonly exchangeDebitDespription = 'Exchange Debit';
    private readonly exchangeCreditDespription = 'Exchange Credit';
    private readonly productIconMapper = new Map<string, string>([
        [ProductGroupCodeConstant.Air, this.airIcon],
        [ProductGroupCodeConstant.Seat, this.seatIcon],
        [ProductGroupCodeConstant.MealAndBeverage, this.mealAndBeverageIcon],
        [ProductGroupCodeConstant.Bag, this.bagIcon],
        [ProductGroupCodeConstant.Wheelchair, this.wheelchairIcon],
        [ProductGroupCodeConstant.Disabled, this.disabledIcon],
        [ProductGroupCodeConstant.Pet, this.petIcon],
        [ProductGroupCodeConstant.Animal, this.petIcon],
        [ProductGroupCodeConstant.Medical, this.medicalIcon],
        [ProductGroupCodeConstant.Comfort, this.comfortIcon],
        [ProductGroupCodeConstant.Others, this.otherIcon],
        [ProductGroupCodeConstant.Individual, this.currencyIcon],
        [ProductGroupCodeConstant.Payment, this.currencyIcon],
        [ProductGroupCodeConstant.Sales, this.currencyIcon],
        [ProductGroupCodeConstant.Service, this.currencyIcon]
    ]);


    constructor(private dateConversion: DateConverterService) { }

    private getDateNumberForCompare(date: Date) {
        return Date.parse(this.dateConversion.convertDateTime(date));
    }

    public getIndividualFinanceAccounting(orderJson: OrderJsonModel,
        individualId: string) : OrderDetailFinanceAccountingView[] {
        if (!orderJson) return;
        const orders = orderJson?.oopsOrder?.order;
        let payments = orderJson?.oopsOrder?.payment?.filter(payment => payment.paymentStatus?.code == PaymentConstant.paid || payment.paymentStatus?.code == PaymentConstant.issued);
        const header = orders?.find(order => order.orderState?.code == OrderStateCode.Header);
        const headerChildren = orders?.filter(order => order.parentOrderId == header?.orderId);
        const headerIndividual = headerChildren?.find(order => order?.orderPartner?.[0]?.individualId == individualId);

        let saleOrder = orders?.filter(order => order.orderState?.code == OrderStateCode.Sales || order.orderState?.code == OrderStateCode.Refund || order.orderState?.code == OrderStateCode.Exchange);
        let saleOrderParentIds = saleOrder?.map(order => order.parentOrderId);
        const individualOrders = orders?.filter(order => saleOrderParentIds?.includes(order.orderId) && order.referenceOrderId == headerIndividual?.orderId);
        let individualOrderIds = individualOrders?.map(order => order.orderId);
        let individualOrderParentIds = individualOrders?.map(order => order.parentOrderId);

        const productItemOrders = orders?.filter(order => individualOrderParentIds.includes(order.orderId));
        const individualSaleOrder = saleOrder?.filter(order =>individualOrderIds.includes(order.parentOrderId));
        payments = this.filterIndividualPayment(payments, individualSaleOrder);
        const ledgers = payments?.flatMap(payment => payment.ledger);

        let sortedTreeNodeDisplay = this.getSortedOrderAndPayment(individualSaleOrder, payments, orders, ledgers);
        let financeTreeNodes = new Array<OrderDetailFinanceAccountingView>()
        sortedTreeNodeDisplay?.forEach((node, index) => {
            switch (node?.code) {
                case OrderStateCode.Sales:
                    this.fillOrderTreeNode(financeTreeNodes, node.id, orders, individualSaleOrder, individualOrders, productItemOrders, ledgers);
                    break;
                case OrderStateCode.Refund:
                    this.fillOrderTreeNode(financeTreeNodes, node.id, orders, individualSaleOrder, individualOrders, productItemOrders, ledgers, true);
                    break;
                case OrderStateCode.Exchange:
                    this.fillExchangeTreeNode(financeTreeNodes, node.id, individualSaleOrder, ledgers);
                    break;
                case this.paymentCode:
                    this.fillPaymentTreeNode(financeTreeNodes, node.id, payments, individualSaleOrder, node.dateTime, orders, sortedTreeNodeDisplay);
                    break;
                default:
                    break;
            }
            this.fillBlankDividerRow(financeTreeNodes, (index + 1).toString());
        });
        this.addTotalBalanceTreeNode(financeTreeNodes);
        return financeTreeNodes;
    }

    private filterIndividualPayment(payments: OrderPaymentModel[], salesOrders: OrderDataModel[]): OrderPaymentModel[] {
        let salesOrderIds = salesOrders?.map(order => order.orderId);
        let individualPayment = payments?.filter(payment => payment.ledger.find(item => salesOrderIds.includes(item.orderId)));
        return individualPayment;
    }

    private getSortedOrderAndPayment(saleOrders: OrderDataModel[], payments: OrderPaymentModel[], orders: OrderDataModel[], ledgers: OrderLedgersModel[]): any[] {
        let displayTreeNodes = new Array();
        
        saleOrders?.forEach(order => {
            displayTreeNodes.push({
                code: order?.orderState?.code,
                id: order?.orderId,
                dateTime: order?.order?.dateTime,
                serviceDateTime: this.getProductServiceDateTime(order, orders)
            });
        })

        payments?.forEach(payment => {
            let paymentDateTime = payment?.paymentStatus?.code == PaymentConstant.issued ? payment?.issue?.dateTime : payment?.payment?.dateTime;
            displayTreeNodes.push({
                code: this.paymentCode,
                id: payment?.paymentId,
                dateTime: paymentDateTime,
                serviceDateTime: paymentDateTime
            });
        })

        return this.sortOrderAndPaymentDisplay(displayTreeNodes, ledgers)
    }

    private sortOrderAndPaymentDisplay(nodes: any[], ledgers: OrderLedgersModel[]): any[] {
        let sortNodes = nodes.sort((a, b) => {
            const aDateTime = this.getDateNumberForCompare(a?.dateTime);
            const bDateTime = this.getDateNumberForCompare(b?.dateTime);
            if (aDateTime == bDateTime) {
                return this.getDateNumberForCompare(a?.serviceDateTime) - this.getDateNumberForCompare(b?.serviceDateTime);
            }
            return aDateTime - bDateTime;
        })
        sortNodes = this.sortExchangeNode(sortNodes, ledgers)
        return sortNodes;
    }

    private sortExchangeNode(nodes: any[], ledgers: OrderLedgersModel[]): any[] {
        let exchangeNodeIds = nodes?.filter(node => node.code == OrderStateCode.Exchange).map(node => node.id);
        if (exchangeNodeIds?.length <= 0) return nodes;
        let exchanges = ledgers?.filter(ledger => exchangeNodeIds.includes(ledger?.orderId));
        exchanges?.forEach(exchange => {
            let salesExchange = ledgers?.find(ledger => ledger.exchangeLedgerId == exchange?.ledgerId);
            let salesExchangeIndex = nodes?.findIndex(node => node.id == salesExchange?.orderId);
            let salesExchangeNode = nodes?.splice(salesExchangeIndex, 1);

            let exchangeIndex = nodes.findIndex(node => node.id == exchange?.orderId);
            nodes.splice(exchangeIndex + 1, 0, ...salesExchangeNode);
        })
        return nodes;
    }

    private getProductServiceDateTime(saleOrder: OrderDataModel, orders: OrderDataModel[]): Date {
        let individualOrder = orders?.find(order => order.orderId == saleOrder?.parentOrderId);
        let productOrder = orders?.find(order => order.orderId == individualOrder?.parentOrderId);
        let refProductOrder = orders?.find(order => order.orderId == productOrder?.referenceOrderId);
        if (refProductOrder?.orderProduct?.[0]?.productCategory?.code != OrderProductCategory.Transport) {
            let parentItemProduct = orders?.find(order => order.orderId == productOrder?.parentOrderId);
            refProductOrder = orders?.find(order => order.orderId == parentItemProduct?.referenceOrderId);
        }

        return this.getFlightEarlyServiceDateTime(refProductOrder?.orderProduct?.[0]);
    }

    private fillBlankDividerRow(financeTreeNodes: OrderDetailFinanceAccountingView[], index: string) {
        financeTreeNodes.push({
            dateTime: null,
            description: null,
            numberOfUnits: null,
            percent: null,
            amount: null,
            debitAmount: null,
            creditAmount: null,
            balanceAmount: null,
            hierarchyId: index,
            parentHierarchyId: null,
            referenceOrderId: null,
            type: null
        })
    }

    private fillOrderTreeNode(financeTreeNodes: OrderDetailFinanceAccountingView[],
        orderId: string,
        orders: OrderDataModel[],
        saleOrders: OrderDataModel[],
        individualOrders: OrderDataModel[],
        productOrders: OrderDataModel[],
        ledgers: OrderLedgersModel[],
        refundFlag: boolean = false) {

        const saleOrder = saleOrders?.find(order => order.orderId == orderId);
        const individualOrder = individualOrders.find(order => order.orderId == saleOrder?.parentOrderId);
        const productOrder = productOrders.find(order => order.orderId == individualOrder?.parentOrderId);
        const refOrderProduct = orders.find(order => order.orderId == productOrder?.referenceOrderId);
        const product = refOrderProduct?.orderProduct?.[0];
        const totalAmount = saleOrder?.orderAmount?.find(amount => amount.orderAmountType?.code == OrderAmountTypeCode.Total)?.sales?.amount ?? 0
        const rootTreeNodes = financeTreeNodes.filter(node => !node.parentHierarchyId && node.type != null);
        const lastestNode = rootTreeNodes[rootTreeNodes.length - 1];
        const saleExchangeLedger = ledgers?.find(ledger => ledger.orderId == saleOrder.orderId && ledger.exchangeLedgerId);
        const creditAmountFlag = refundFlag ? refundFlag : saleExchangeLedger?.ledgerTransaction?.code == PaymentConstant.cr;

        financeTreeNodes.push({
            dateTime: this.dateConversion.formatSystemDateTime(saleOrder?.order?.dateTime),
            description: this.getProductDescription(product),
            numberOfUnits: individualOrder.numberOfUnits,
            percent: null,
            amount: null,
            debitAmount: !creditAmountFlag ? totalAmount : null,
            creditAmount: creditAmountFlag ? totalAmount : null,
            balanceAmount: creditAmountFlag ? (lastestNode?.balanceAmount ?? 0) + totalAmount : (lastestNode?.balanceAmount ?? 0) - totalAmount,
            hierarchyId: orderId,
            parentHierarchyId: null,
            referenceOrderId: saleOrder?.referenceOrderId,
            icon: this.getSalesProductIcon(saleExchangeLedger, product, refundFlag),
            iconClass: saleExchangeLedger?.exchangeLedgerId || refundFlag ? this.iconClassRed : null,
            type: FinanceAccountionType.Product
        })
        const childHeirarchyId = `${this.prefixPriceDetailId}${orderId}`;
        this.fillPriceDetailNode(financeTreeNodes, orderId, childHeirarchyId);
        this.fillProductOrderAmountDetail(financeTreeNodes, saleOrder, childHeirarchyId);

        this.checkExchangeProduct(financeTreeNodes, saleOrder, saleExchangeLedger);
        this.checkCancelProduct(financeTreeNodes, saleOrder, saleOrder?.orderAmount)
    }

    private getProductDescription(orderProduct: OrderProductModel): string { 
        if (orderProduct?.productCategory?.code != OrderProductCategory.Transport) return orderProduct?.orderProductName;
        return this.getFlightDescription(orderProduct);
    }

    private getSalesProductIcon(saleExchangeLedger: OrderLedgersModel, product: OrderProductModel, refundFlag: boolean) {
        if (refundFlag) return this.deleteIcon;
        if (saleExchangeLedger?.exchangeLedgerId) return this.addShoppingCartIcon;
        return this.productIconMapper.get(product?.productGroup?.code);
    }

    private getFlightDescription(orderProduct: OrderProductModel): string {
        let flightDateTime = this.dateConversion.formatSystemShortDate(this.getFlightEarlyServiceDateTime(orderProduct));
        let flightRoute = this.getFlightRoute(orderProduct);
        let flightNumber = this.getFlightNumber(orderProduct);
        return `${flightDateTime} ${flightRoute} ${flightNumber}`;
    }

    private getFlightEarlyServiceDateTime(orderProduct: OrderProductModel): Date {
        let productDateTimes = orderProduct?.orderProductDateTime;
        const earlyProductDateTime = productDateTimes
            ?.reduce((a, b) => a.localDateTime < b.localDateTime ? a : b);
        return earlyProductDateTime?.localDateTime;
    }

    private getFlightRoute(orderProduct: OrderProductModel): string {
        const firstRecordIndex: number = 0;
        const sortedTransportPoints = orderProduct?.orderProductPoint
            .sort((a, b) => a.pointSequence - b.pointSequence);
        let transportPointResult: string = "";
        for (let i = 0; i < sortedTransportPoints.length; i++) {
            const transportPoint = sortedTransportPoints[i];
            if (firstRecordIndex !== i) {
                transportPointResult += ` - ${transportPoint.location.name} (${transportPoint.location.code})`;
                continue;
            }
            transportPointResult += `${transportPoint.location.name} (${transportPoint.location.code})`;
        }
        return transportPointResult;
    }

    private getFlightNumber(orderProduct: OrderProductModel): string {
        const orderProductNumbers = orderProduct?.orderProductNumber;
        if (!orderProductNumbers || orderProductNumbers.length === 0) {
            return "";
        }
        const marketingProductNumber = orderProductNumbers.find(x => x.productNumberType.code == ProductNumberType.marketing);
        if (marketingProductNumber) {
            return `${marketingProductNumber.provider.code} ${marketingProductNumber.productNumber}`;
        }
        const operatingProductNumber = orderProductNumbers.find(x => x.productNumberType.code == ProductNumberType.operating);
        return `${operatingProductNumber.provider.code} ${operatingProductNumber.productNumber}`;
    }

    private fillPriceDetailNode(financeTreeNodes: OrderDetailFinanceAccountingView[], saleOrderId: string, hierarchyId: string) {
        financeTreeNodes.push({
            dateTime: null,
            description: 'Price Details',
            numberOfUnits: null,
            percent: null,
            amount: null,
            debitAmount: null,
            creditAmount: null,
            balanceAmount: null,
            hierarchyId: hierarchyId,
            parentHierarchyId: saleOrderId,
            type: FinanceAccountionType.Price
        });
    }

    private fillProductOrderAmountDetail(financeTreeNodes: OrderDetailFinanceAccountingView[], saleOrders: OrderDataModel, parentId: string, creditFlag: boolean = false) {
        let orderAmounts = saleOrders?.orderAmount?.filter(order => order.orderAmountType?.code != OrderAmountTypeCode.Total);
        orderAmounts = this.sortOrderAmounts(orderAmounts);
        let groupAmounts = this.groupOrderAmountByType(orderAmounts);

        let keys = Object.keys(groupAmounts);
        keys?.forEach(key => {
            this.fillGroupOrderAmount(financeTreeNodes, groupAmounts[key], parentId, creditFlag)
        })
    }

    private sortOrderAmounts(amounts: OrderAmountModel[]): OrderAmountModel[] {
        return amounts?.sort((a, b) => {
            const isVatA = a.priceRuleType?.code == PriceRuleTypeNames.VAT;
            const isVatB = b.priceRuleType?.code == PriceRuleTypeNames.VAT;
            if (isVatA && !isVatB) return 1;
            if (!isVatA && isVatB) return -1;

            if (a.displaySequence !== b.displaySequence) {
                return a.displaySequence - b.displaySequence;
            }

            const displayNameA = a.priceRuleDisplay?.name ?? '';
            const displayNameB = b.priceRuleDisplay?.name ?? '';
            if (displayNameA !== displayNameB) {
                return displayNameA.localeCompare(displayNameB);
            }

            return (a.priceRule?.name ?? '').localeCompare(b.priceRule?.name ?? '');
        });
    }

    private groupOrderAmountByType(amounts: OrderAmountModel[]): any {
        let groupAmounts = amounts?.reduce((groups, item) => {
            (groups[item.orderAmountType?.code] ||= []).push(item)
            return groups;
        }, {})
        return groupAmounts;
    }

    private fillGroupOrderAmount(financeTreeNodes: OrderDetailFinanceAccountingView[], amounts: OrderAmountModel[], parentId: string, creditFlag: boolean = false) {
        let index = 0;
        amounts?.forEach(amount => {
            financeTreeNodes.push({
                dateTime: null,
                description: index === 0 ? amount.orderAmountType?.name : '',
                subDescription: amount.priceRuleDisplay?.name ?? amount.priceRule?.name,
                numberOfUnits: null,
                percent: amount.price?.percentage,
                amount: amount.ledgerTransactionCode == PaymentConstant.cr || creditFlag || amount.orderAmountType?.code == OrderAmountTypeCode.Discount ? -amount.price?.amount : amount.price?.amount,
                debitAmount: null,
                creditAmount: null,
                balanceAmount: null,
                hierarchyId: creditFlag ? `${this.prefixExchangeId}${amount.orderAmountId}` : amount.orderAmountId,
                parentHierarchyId: parentId,
                type: FinanceAccountionType.Price,
                priceRuleCode: amount.priceRuleDisplay?.code ?? amount.priceRule?.code
            })
            index += 1;
        })
    }

    private fillPaymentTreeNode(financeTreeNodes: OrderDetailFinanceAccountingView[],
        paymentId: string,
        payments: OrderPaymentModel[],
        saleOrders: OrderDataModel[],
        dateTime: Date,
        orders: OrderDataModel[],
        sortedTreeNode: any[]) {

        const payment = payments.find(payment => payment.paymentId == paymentId);
        const paymentDesc = `${payment?.formOfPayment?.name} ${payment?.document?.numberDisplay ?? payment?.payment?.numberDisplay}`
        const saleOrderIds = saleOrders?.map(order => order.orderId);
        let individualLedgers = payment?.ledger?.filter(ledger => saleOrderIds.includes(ledger.orderId) && !ledger.exchangeLedgerId && ledger.exchangeFlag !== true);
        individualLedgers = individualLedgers.sort((a, b) => {
            let aSortedIndex = sortedTreeNode.findIndex(item => item.id == a.orderId);
            let bSortedIndex = sortedTreeNode.findIndex(item => item.id == b.orderId);

            return aSortedIndex - bSortedIndex;
        });
        const rootTreeNodes = financeTreeNodes.filter(node => !node.parentHierarchyId && node.type != null);
        const lastestNode = rootTreeNodes[rootTreeNodes.length - 1];
        const paymentLedger = payment?.ledger?.find(ledger => ledger.paymentId);
        const sumOfLedger = individualLedgers?.reduce((sum, ledger) => sum + ledger.transaction?.amount, 0);
        const creditPaymentFlag = paymentLedger.ledgerTransaction?.code == PaymentConstant.cr;

        financeTreeNodes.push({
            dateTime: this.dateConversion.formatSystemDateTime(dateTime),
            description: paymentDesc,
            numberOfUnits: null,
            percent: null,
            amount: paymentLedger.transaction?.amount,
            debitAmount: !creditPaymentFlag ? sumOfLedger : null,
            creditAmount: creditPaymentFlag ? sumOfLedger : null,
            balanceAmount: creditPaymentFlag ? (lastestNode?.balanceAmount ?? 0) + sumOfLedger : (lastestNode?.balanceAmount ?? 0) - sumOfLedger,
            hierarchyId: paymentId,
            parentHierarchyId: null,
            type: creditPaymentFlag ? FinanceAccountionType.Payment : FinanceAccountionType.Refund
        })
        this.fillLedgerTreeNode(financeTreeNodes, paymentId, individualLedgers, orders);
    }

    private fillLedgerTreeNode(financeTreeNodes: OrderDetailFinanceAccountingView[], paymentId: string, ledgers: OrderLedgersModel[], orders: OrderDataModel[]) {
        ledgers?.forEach(ledger => {
            const productTreeNode = financeTreeNodes?.find(node => node.hierarchyId == ledger?.orderId);
            financeTreeNodes.push({
                dateTime: null,
                description: productTreeNode?.description,
                numberOfUnits: productTreeNode?.numberOfUnits,
                percent: null,
                amount: ledger?.transaction?.amount,
                debitAmount: null,
                creditAmount: null,
                balanceAmount: null,
                hierarchyId: ledger.ledgerId,
                parentHierarchyId: paymentId,
                icon: this.getLedgerProductGroupIcon(productTreeNode, orders),
                type: FinanceAccountionType.Ledger
            })
        })
    }

    private getLedgerProductGroupIcon(productTreeNode: OrderDetailFinanceAccountingView, orders: OrderDataModel[]): string {
        if (productTreeNode?.type != FinanceAccountionType.Product && productTreeNode?.type != FinanceAccountionType.Voucher) return null;
        if (productTreeNode?.icon != this.deleteIcon && productTreeNode?.icon != this.addShoppingCartIcon) return productTreeNode?.icon
        const saleOrder = orders.find(order => order.orderId == productTreeNode?.hierarchyId);
        const individualOrder = orders.find(order => order.orderId == saleOrder?.parentOrderId);
        const productOrder = orders.find(order => order.orderId == individualOrder?.parentOrderId);
        const refOrderProduct = orders.find(order => order.orderId == productOrder?.referenceOrderId);
        const product = refOrderProduct?.orderProduct?.[0];
        return this.productIconMapper.get(product?.productGroup?.code);
    }

    private fillExchangeTreeNode(financeTreeNodes: OrderDetailFinanceAccountingView[], exchangeId: string, saleOrders: OrderDataModel[], ledgers: OrderLedgersModel[]) {
        let exchange = saleOrders.find(order => order.orderId == exchangeId);
        let ledger = ledgers.find(ledger => ledger.orderId == exchangeId);
        let exchangeCreditSales = saleOrders.find(order => order.orderState?.code == OrderStateCode.Sales && order.parentOrderId == exchange.parentOrderId);
        this.fillExchangeCreditTreeNode(financeTreeNodes, exchangeId, ledger, exchangeCreditSales, exchange);
        const ledgerAmount = ledger.transaction?.amount ?? 0;
        const rootTreeNodes = financeTreeNodes.filter(node => !node.parentHierarchyId && node.type != null);
        const lastestNode = rootTreeNodes[rootTreeNodes.length - 1];

        this.fillBlankDividerRow(financeTreeNodes, `${this.prefixBlankDividerId}${exchangeCreditSales.orderId}`);
        financeTreeNodes.push({
            dateTime: this.dateConversion.formatSystemDateTime(exchange.order?.dateTime),
            description: this.exchangeDebitDespription,
            numberOfUnits: null,
            percent: null,
            amount: null,
            debitAmount: ledgerAmount,
            creditAmount: null,
            balanceAmount: (lastestNode?.balanceAmount ?? 0) - ledgerAmount,
            hierarchyId: exchangeId,
            parentHierarchyId: null,
            icon: this.exchangeIcon,
            type: FinanceAccountionType.Exchange
        })
    }

    private fillExchangeCreditTreeNode(financeTreeNodes: OrderDetailFinanceAccountingView[], exchangeId: string, ledger: OrderLedgersModel, saleOrder: OrderDataModel, exhangeOrder: OrderDataModel) {
        const rootTreeNodes = financeTreeNodes.filter(node => !node.parentHierarchyId && node.type != null);
        const lastestNode = rootTreeNodes[rootTreeNodes.length - 1];
        const exchangeCreditId = `${exchangeId}_${ledger.ledgerId}`
        const ledgerAmount = ledger.transaction?.amount ?? 0;

        const productNode = cloneDeep(financeTreeNodes.find(node => node.hierarchyId == saleOrder.orderId));
        if (!productNode) return;
        productNode.dateTime = this.dateConversion.formatSystemDateTime(ledger?.transactionDateTime);
        productNode.hierarchyId = exchangeCreditId;
        productNode.icon = this.deleteIcon;
        productNode.iconClass = this.iconClassRed;
        productNode.debitAmount = null;
        productNode.creditAmount = ledgerAmount;
        productNode.balanceAmount = (lastestNode?.balanceAmount ?? 0) + ledgerAmount;
        financeTreeNodes.push(productNode)

        const priceDetailId = `${this.prefixPriceDetailId}${exchangeCreditId}`
        this.fillPriceDetailNode(financeTreeNodes, exchangeCreditId, priceDetailId);
        this.fillProductOrderAmountDetail(financeTreeNodes, exhangeOrder, priceDetailId, true);
    }

    private addTotalBalanceTreeNode(financeTreeNodes: OrderDetailFinanceAccountingView[]) {
        let debit = financeTreeNodes?.reduce((sum, node) => sum += (node.debitAmount ?? 0), 0)
        let credit = financeTreeNodes?.reduce((sum, node) => sum += (node.creditAmount ?? 0), 0)
        let balance = credit - debit;

        financeTreeNodes.push({
            dateTime: null,
            description: 'Balance',
            numberOfUnits: null,
            percent: null,
            amount: null,
            debitAmount: debit,
            creditAmount: credit,
            balanceAmount: balance,
            hierarchyId: 'BALANCE',
            parentHierarchyId: null,
            type: FinanceAccountionType.Balance
        })
    }

    private checkExchangeProduct(financeTreeNodes: OrderDetailFinanceAccountingView[], saleOrder: OrderDataModel, saleLedger: OrderLedgersModel) {
        if (!saleLedger?.exchangeLedgerId) return;
        const rootTreeNodes = financeTreeNodes.filter(node => !node.parentHierarchyId && node.type != null);
        const lastestNode = rootTreeNodes[rootTreeNodes.length - 1];

        this.fillBlankDividerRow(financeTreeNodes, `${this.prefixBlankDividerId}${saleOrder.orderId}`);
        financeTreeNodes.push({
            dateTime: this.dateConversion.formatSystemDateTime(saleLedger.transactionDateTime),
            description: this.exchangeCreditDespription,
            numberOfUnits: null,
            percent: null,
            amount: null,
            debitAmount: null,
            creditAmount: saleLedger.transaction?.amount,
            balanceAmount: (lastestNode.balanceAmount ?? 0) + saleLedger.transaction?.amount,
            hierarchyId: `${this.prefixExchangeId}${saleOrder.orderId}`,
            parentHierarchyId: null,
            icon: this.exchangeIcon,
            type: FinanceAccountionType.Exchange
        })
    }

    private fillCancelProductFromProductTree(financeTreeNodes: OrderDetailFinanceAccountingView[], displayNode: any, salesOrders: OrderDataModel[]) {
        const salesOrder = salesOrders.find(order => order.orderId == displayNode?.id);
        const totalAmount = salesOrder?.orderAmount?.find(amount => amount.orderAmountType?.code == OrderAmountTypeCode.Total);
        this.fillCancelProduct(financeTreeNodes, salesOrder, totalAmount);
    }

    private checkCancelProduct(financeTreeNodes: OrderDetailFinanceAccountingView[], saleOrder: OrderDataModel, orderAmounts: OrderAmountModel[]) {
        const totalAmount = orderAmounts?.find(amount => amount.orderAmountType?.code == OrderAmountTypeCode.Total);
        if (!totalAmount?.cancel?.dateTime) return;
        this.fillCancelProduct(financeTreeNodes, saleOrder, totalAmount);
    }

    private fillCancelProduct(financeTreeNodes: OrderDetailFinanceAccountingView[], saleOrder: OrderDataModel, totalAmount: OrderAmountModel): boolean {
        const parentHierarchyId = `${this.prefixPriceDetailId}${saleOrder.orderId}`;
        const cancelProductNode = cloneDeep(financeTreeNodes?.find(node => node.hierarchyId == saleOrder.orderId));
        const priceDetaiNode = cloneDeep(financeTreeNodes?.find(node => node.hierarchyId == parentHierarchyId));
        const amountNodes = cloneDeep(financeTreeNodes?.filter(node => node.parentHierarchyId == parentHierarchyId));
        const cancelParentHierarchyId = `${this.prefixCancelId}${parentHierarchyId}`
        if (!priceDetaiNode) return;
        priceDetaiNode.hierarchyId = cancelParentHierarchyId

        amountNodes.forEach(node => {
            node.amount = -node.amount
            node.parentHierarchyId = cancelParentHierarchyId
            node.hierarchyId = `${this.prefixCancelId}${node.hierarchyId}`
        });
        cancelProductNode.dateTime = this.dateConversion.formatSystemDateTime(totalAmount?.cancel?.dateTime);
        cancelProductNode.creditAmount = cloneDeep(cancelProductNode.debitAmount);
        cancelProductNode.debitAmount = null;
        cancelProductNode.balanceAmount = (cancelProductNode.balanceAmount ?? 0) + (cancelProductNode?.creditAmount ?? 0);
        cancelProductNode.hierarchyId = `${this.prefixCancelId}${cancelProductNode.hierarchyId}`;
        cancelProductNode.icon = this.deleteIcon;
        cancelProductNode.iconClass = this.iconClassRed;
        priceDetaiNode.parentHierarchyId = cancelProductNode.hierarchyId;

        const cancelProductNodes = [cancelProductNode, priceDetaiNode, ...amountNodes];
        this.fillBlankDividerRow(financeTreeNodes, `${this.prefixBlankDividerId}${cancelParentHierarchyId}`);
        financeTreeNodes.push(...cancelProductNodes);
    }

    public getProductFinanceAccounting(orderJson: OrderJsonModel, productId: string, partnerOrderId: string): OrderDetailFinanceAccountingView[] {
        const orders = orderJson?.oopsOrder?.order;
        const payments = orderJson?.oopsOrder?.payment?.filter(payment => payment.paymentStatus?.code == PaymentConstant.paid || payment.paymentStatus?.code == PaymentConstant.issued);
        const header = orders?.find(order => order.orderState?.code == OrderStateCode.Header);
        const headerChildren = orders?.filter(order => order.parentOrderId == header?.orderId);
        const headerProduct = headerChildren?.find(order => order?.orderProduct?.[0].product?.id == productId);
        const orderProduct = headerProduct?.orderProduct?.[0];

        const itemProductOrder = orders?.find(order => order.orderState?.code == OrderStateCode.Product && order.referenceOrderId == headerProduct?.orderId);
        const productIndividualOrders = orders?.filter(order => order.parentOrderId == itemProductOrder?.orderId);
        const productIndividualOrderIds = productIndividualOrders?.map(order => order.orderId);
        const saleOrders = this.getProductSalesOrders(orders, orderProduct, itemProductOrder, productIndividualOrderIds, partnerOrderId);
        const ledgers = payments?.flatMap(payment => payment.ledger);

        let sortedTreeNodeDisplay = this.getSortedProductOrderAndPayment(saleOrders, payments, orders, ledgers);
        let financeTreeNodes = new Array<OrderDetailFinanceAccountingView>()
        sortedTreeNodeDisplay?.forEach((node, index) => {
            let addSuccess = true;
            switch (node?.code) {
                case OrderStateCode.Sales:
                    this.fillProductOrderTreeNode(financeTreeNodes, node.id, orders, saleOrders, ledgers, orderProduct);
                    break;
                case OrderStateCode.Refund:
                    this.fillProductOrderTreeNode(financeTreeNodes, node.id, orders, saleOrders, ledgers, orderProduct, true);
                    break;
                case OrderStateCode.Exchange:
                    this.fillExchangeTreeNode(financeTreeNodes, node.id, saleOrders, ledgers);
                    break;
                case this.paymentCode:
                    addSuccess = this.fillProductPaymentTreeNode(financeTreeNodes, node.id, payments, saleOrders, node.dateTime, orders, orderProduct);
                    break;
                case OrderSalesStatusCode.Cancel:
                    this.fillCancelProductFromProductTree(financeTreeNodes, node, saleOrders);
                    break;
                default:
                    break;
            }
            if (addSuccess && financeTreeNodes?.length > 0) this.fillBlankDividerRow(financeTreeNodes, (index + 1).toString());
        });
        this.addTotalBalanceTreeNode(financeTreeNodes);
        return financeTreeNodes;
    }

    private getProductSalesOrders(orders: OrderDataModel[], headerProductOrder: OrderProductModel, itemProductOrder: OrderDataModel, productIndividualOrderIds: string[], partnerOrderId: string) {
        let allSaleOrders = orders?.filter(order => order.orderState?.code == OrderStateCode.Sales || order.orderState?.code == OrderStateCode.Refund || order.orderState?.code == OrderStateCode.Exchange);
        
        if (headerProductOrder?.productCategory?.code == ProductCategoryConstant.Voucher) {
            return allSaleOrders.filter(order => order.parentOrderId == itemProductOrder.orderId);
        }
        if (headerProductOrder?.productCategory?.code == ProductCategoryConstant.Transport) {
            return allSaleOrders.filter(order => productIndividualOrderIds?.includes(order.parentOrderId));
        }
        let individualOrder = orders?.find(order => order.parentOrderId == itemProductOrder?.orderId);
        return allSaleOrders?.filter(order => order.parentOrderId == individualOrder?.orderId);
    }

    private getSortedProductOrderAndPayment(saleOrders: OrderDataModel[], payments: OrderPaymentModel[], orders: OrderDataModel[], ledgers: OrderLedgersModel[]): any[] {
        let displayTreeNodes = new Array();
        
        saleOrders?.forEach(order => {
            let individualItemOrder = orders.find(item => item.orderId == order.parentOrderId);
            let refOrder = orders.find(item => item.orderId == individualItemOrder.referenceOrderId);
            let orderPartnerName = refOrder?.orderPartner?.[0]?.orderPartnerName;
            displayTreeNodes.push({
                code: order?.orderState?.code,
                firstName: orderPartnerName?.firstName,
                lastName: orderPartnerName?.lastName,
                id: order?.orderId,
                dateTime: order?.order?.dateTime,
                serviceDateTime: this.getProductServiceDateTime(order, orders)
            });
            this.checkForCancelSalesOrderForProductTree(displayTreeNodes, orders, order, individualItemOrder, orderPartnerName);
        })

        payments?.forEach(payment => {
            let paymentDateTime = payment?.paymentStatus?.code == PaymentConstant.issued ? payment?.issue?.dateTime : payment?.payment?.dateTime;
            displayTreeNodes.push({
                code: this.paymentCode,
                id: payment?.paymentId,
                dateTime: paymentDateTime,
                serviceDateTime: paymentDateTime
            });
        })

        return this.sortProductOrderAndPaymentDisplay(displayTreeNodes, ledgers)
    }

    private sortProductOrderAndPaymentDisplay(nodes: any[], ledgers: OrderLedgersModel[]): any[] {
        let sortNodes = this.sortProductOrderAndPaymentByDateTime(nodes);
        let groupedNode = this.groupProductOrderAndPaymentByDateTime(sortNodes);
        sortNodes = this.sortGroupedProductOrderAndPaymentByName(groupedNode);
        return sortNodes;
    }

    private sortGroupedProductOrderAndPaymentByName(groupedNode : any): any[] {
        let keys = Object.keys(groupedNode);
        keys.forEach(key => {
            groupedNode[key] = this.sortProductOrderAndPaymentByName(groupedNode[key]);
        })
        return keys.reduce((list, key) => {
            list.push(groupedNode[key]);
            return list.flat();
        }, [])
    }

    private sortProductOrderAndPaymentByName(nodes: any[]): any[] {
        let sortNodes = nodes.sort((a, b) => {
            if (a.lastName == b.lastName) {
                return a.firstName?.localeCompare(b.firstName);
            }
            return a.lastName?.localeCompare(b.lastName);
        })
        return sortNodes;
    }

    private sortProductOrderAndPaymentByDateTime(nodes: any[]): any[] {
        let sortNodes = nodes.sort((a, b) => {
            const aDateTime = this.getDateNumberForCompare(a?.dateTime);
            const bDateTime = this.getDateNumberForCompare(b?.dateTime);
            if (aDateTime == bDateTime) {
                return this.getDateNumberForCompare(a?.serviceDateTime) - this.getDateNumberForCompare(b?.serviceDateTime);
            }
            return aDateTime - bDateTime;
        })
        return sortNodes;
    }

    private groupProductOrderAndPaymentByDateTime(nodes: any[]): any {
        let groupedNode = nodes.reduce((groups, item) => {
            let key = `${item.dateTime}_${item.serviceDateTime}`;
            (groups[key] ||= []).push(item);
            return groups;
        }, {})
        return groupedNode;
    }

    private checkForCancelSalesOrderForProductTree(nodes: any[], orders: OrderDataModel[], salesOrder: OrderDataModel, individualOrder: OrderDataModel, orderPartnerName: OrderPartnerNameModel) {
        if (individualOrder.orderSalesStatus?.code != OrderSalesStatusCode.Cancel) return;
        let individualChildrenOrder = orders?.filter(order => order.parentOrderId == individualOrder?.orderId);
        if (individualChildrenOrder.find(order => order.orderState.code == OrderStateCode.Refund || order.orderState.code == OrderStateCode.Exchange)) return;
        const cancelTotalAmount = salesOrder.orderAmount?.find(amount => amount.orderAmountType.code == OrderAmountTypeCode.Total);
        let cancelDateTime = this.dateConversion.formatSystemDateTime(cancelTotalAmount?.cancel?.dateTime);
        const salesNode = nodes.find(node => node.id == salesOrder?.orderId)
        nodes.push({
            code: OrderSalesStatusCode.Cancel,
            firstName: orderPartnerName?.firstName,
            lastName: orderPartnerName?.lastName,
            id: salesOrder?.orderId,
            dateTime: new Date(cancelDateTime),
            serviceDateTime: salesNode?.serviceDateTime
        });
    }

    private fillProductOrderTreeNode(financeTreeNodes: OrderDetailFinanceAccountingView[],
        orderId: string,
        orders: OrderDataModel[],
        saleOrders: OrderDataModel[],
        ledgers: OrderLedgersModel[],
        orderProduct: OrderProductModel,
        refundFlag: boolean = false) {

        const saleOrder = saleOrders?.find(order => order.orderId == orderId);
        const individualOrder = orders.find(order => order.orderId == saleOrder.parentOrderId);
        const refIndividualOrder = orders.find(order => order.orderId == individualOrder.referenceOrderId);
        const orderPartner = refIndividualOrder?.orderPartner?.[0];
        const totalAmount = saleOrder?.orderAmount?.find(amount => amount.orderAmountType?.code == OrderAmountTypeCode.Total)?.sales?.amount ?? 0
        const rootTreeNodes = financeTreeNodes.filter(node => !node.parentHierarchyId && node.type != null);
        const lastestNode = rootTreeNodes[rootTreeNodes.length - 1];
        const saleExchangeLedger = ledgers?.find(ledger => ledger.orderId == saleOrder.orderId && ledger.exchangeLedgerId);
        const creditAmountFlag = refundFlag ? refundFlag : saleExchangeLedger?.ledgerTransaction?.code == PaymentConstant.cr;

        financeTreeNodes.push({
            dateTime: this.dateConversion.formatSystemDateTime(saleOrder?.order?.dateTime),
            description: this.getProductIndividualDesc(orderPartner, orderProduct),
            numberOfUnits: individualOrder.numberOfUnits,
            percent: null,
            amount: null,
            debitAmount: !creditAmountFlag ? totalAmount : null,
            creditAmount: creditAmountFlag ? totalAmount : null,
            balanceAmount: creditAmountFlag ? (lastestNode?.balanceAmount ?? 0) + totalAmount : (lastestNode?.balanceAmount ?? 0) - totalAmount,
            hierarchyId: orderId,
            parentHierarchyId: null,
            referenceOrderId: saleOrder?.referenceOrderId,
            type: orderProduct?.productCategory?.code == ProductCategoryConstant.Voucher ? FinanceAccountionType.Product : FinanceAccountionType.Individual,
            individualIconClass: orderProduct?.productCategory?.code != ProductCategoryConstant.Voucher ? this.getPassengerClassName(orderPartner) : null,
            icon: refundFlag ? this.deleteIcon : orderProduct?.productCategory?.code == ProductCategoryConstant.Voucher ? this.getVoucherIcon(orderProduct) : null,
            iconClass: refundFlag ? this.iconClassRed : null,
        })
        const childHeirarchyId = `${this.prefixPriceDetailId}${orderId}`;
        this.fillPriceDetailNode(financeTreeNodes, orderId, childHeirarchyId);
        this.fillProductOrderAmountDetail(financeTreeNodes, saleOrder, childHeirarchyId);

        this.checkExchangeProduct(financeTreeNodes, saleOrder, saleExchangeLedger);
    }

    private getVoucherIcon(orderProduct: OrderProductModel): string {
        switch (orderProduct.productGroup?.code) {
            case VoucherProductGroupCode.Discount:
                return this.discountIcon;
            case VoucherProductGroupCode.Exchange:
                return this.exchangeIcon;
            case VoucherProductGroupCode.Gift:
                return this.giftIcon;
            case VoucherProductGroupCode.Promotion:
                return this.promotionIcon;
            default:
                return null;
        }
    }

    private getPassengerClassName(orderPartner: OrderPartnerModel): string {
        let className = "passenger-no-gender";

        switch (orderPartner?.individualAgeGroup?.code) {
            case AgeGroupCodeFull.Adult:
                className = orderPartner?.gender?.code == "F" ? "passenger-female" : "passenger-male";
                break;
            case AgeGroupCodeFull.Child:
                className = orderPartner.gender?.code == "F" ? "passenger-girl" : "passenger-child";
                break;
            case AgeGroupCodeFull.Infant:
                className = "passenger-infant"
                break;
            default:
                break;
        }
        return className;
    }

    private getProductIndividualDesc(orderPartner: OrderPartnerModel, orderProduct: OrderProductModel) {
        if (orderProduct?.productCategory?.code == ProductCategoryConstant.Voucher) {
            return orderProduct?.orderProductName;
        }
        return this.getIndividualDesc(orderPartner);
    }

    private getIndividualDesc(orderPartner: OrderPartnerModel) {
        return `${orderPartner?.orderPartnerName?.lastName} ${orderPartner?.orderPartnerName?.firstName} ${orderPartner?.orderPartnerName?.individualTitle?.name ?? ''}`;
    }

    private fillProductPaymentTreeNode(financeTreeNodes: OrderDetailFinanceAccountingView[],
        paymentId: string,
        payments: OrderPaymentModel[],
        saleOrders: OrderDataModel[],
        dateTime: Date,
        orders: OrderDataModel[],
        orderProduct: OrderProductModel): boolean {

        const payment = payments.find(payment => payment.paymentId == paymentId);
        const paymentDesc = `${payment?.formOfPayment?.name} ${payment?.document?.numberDisplay ?? payment?.payment?.numberDisplay}`
        const saleOrderIds = saleOrders?.map(order => order.orderId);
        let individualLedgers = payment?.ledger?.filter(ledger => saleOrderIds.includes(ledger.orderId) && !ledger.exchangeLedgerId && ledger.exchangeFlag !== true);
        if (individualLedgers?.length <= 0) return false;
        const rootTreeNodes = financeTreeNodes.filter(node => !node.parentHierarchyId && node.type != null);
        const lastestNode = rootTreeNodes[rootTreeNodes.length - 1];
        const paymentLedger = payment?.ledger?.find(ledger => ledger.paymentId);
        const sumOfLedger = individualLedgers?.reduce((sum, ledger) => sum + ledger.transaction?.amount, 0);
        const creditPaymentFlag = paymentLedger.ledgerTransaction?.code == PaymentConstant.cr;

        financeTreeNodes.push({
            dateTime: this.dateConversion.formatSystemDateTime(dateTime),
            description: paymentDesc,
            numberOfUnits: null,
            percent: null,
            amount: paymentLedger.transaction?.amount,
            debitAmount: !creditPaymentFlag ? sumOfLedger : null,
            creditAmount: creditPaymentFlag ? sumOfLedger : null,
            balanceAmount: creditPaymentFlag ? (lastestNode?.balanceAmount ?? 0) + sumOfLedger : (lastestNode?.balanceAmount ?? 0) - sumOfLedger,
            hierarchyId: paymentId,
            parentHierarchyId: null,
            type: creditPaymentFlag ? FinanceAccountionType.Payment : FinanceAccountionType.Refund
        })
        individualLedgers = this.sortLedgerForProductTree(financeTreeNodes, individualLedgers);
        this.fillLedgerTreeNodeForProductTree(financeTreeNodes, paymentId, individualLedgers, orders, saleOrders, orderProduct);
        return true;
    }

    private sortLedgerForProductTree(financeTreeNodes: OrderDetailFinanceAccountingView[], individualLedgers: OrderLedgersModel[]): OrderLedgersModel[] {
       return individualLedgers.sort((a, b) => {
            let aSortedIndex = financeTreeNodes.findIndex(item => item.hierarchyId == a.orderId);
            let bSortedIndex = financeTreeNodes.findIndex(item => item.hierarchyId == b.orderId);

            return aSortedIndex - bSortedIndex;
        });
    }

    private fillLedgerTreeNodeForProductTree(financeTreeNodes: OrderDetailFinanceAccountingView[], paymentId: string, ledgers: OrderLedgersModel[], orders: OrderDataModel[], saleOrders: OrderDataModel[], orderProduct: OrderProductModel) {
        ledgers?.forEach(ledger => {
            let salesOrder = saleOrders?.find(order => order.orderId == ledger?.orderId);
            let individualOrder = orders?.find(order => order.orderId == salesOrder?.parentOrderId);
            let individualRef = orders?.find(order => order.orderId == individualOrder?.referenceOrderId);
            financeTreeNodes.push({
                dateTime: null,
                description: this.getProductIndividualDesc(individualRef.orderPartner?.[0], orderProduct),
                numberOfUnits: individualOrder?.numberOfUnits,
                percent: null,
                amount: ledger?.transaction?.amount,
                debitAmount: null,
                creditAmount: null,
                balanceAmount: null,
                hierarchyId: ledger.ledgerId,
                parentHierarchyId: paymentId,
                individualIconClass: orderProduct?.productCategory?.code != ProductCategoryConstant.Voucher ? this.getPassengerClassName(individualRef.orderPartner?.[0]) : null,
                icon: orderProduct?.productCategory?.code == ProductCategoryConstant.Voucher ? this.getVoucherIcon(orderProduct) : null,
                type: FinanceAccountionType.Ledger
            })
        })
    }

    public getPaymentFinanceAccounting(orderJson: OrderJsonModel,
        paymentId: string): OrderDetailFinanceAccountingView[] {
        
        if (!orderJson) return;
        const orders = orderJson?.oopsOrder?.order;
        const payments = orderJson?.oopsOrder?.payment;
        const payment = payments.find(item => item.paymentId == paymentId);
        const ledgers = payment?.ledger?.filter(item => item.parentLedgerId != null);

        let financeTreeNodes = new Array<OrderDetailFinanceAccountingView>();
        let groupedProduct = this.getGroupProductSalesOrder(orders, ledgers);
        let paymentNodes = this.getPaymentNode(payments, paymentId);
        let sortedTreeNodeDisplay = this.getSortedPaymentOrderTreeNodes(groupedProduct.groupedProduct, paymentNodes, ledgers);
        sortedTreeNodeDisplay?.forEach((node, index) => {
            switch (node?.code) {
                case OrderStateCode.Refund:
                    this.fillProductForPaymentTreeNode(financeTreeNodes, node, orders, groupedProduct?.salesOrder, groupedProduct?.individualOrders, ledgers, true);
                    break;
                case OrderStateCode.Exchange:
                    this.fillExchangeProductForPaymentTree(financeTreeNodes, node, groupedProduct?.salesOrder, ledgers)
                    break;
                case OrderStateCode.Product:
                    this.fillProductForPaymentTreeNode(financeTreeNodes, node, orders, groupedProduct?.salesOrder, groupedProduct?.individualOrders, ledgers);
                    break;
                case this.paymentCode:
                    this.fillPaymentForPaymentTree(financeTreeNodes, node.id, payments, groupedProduct.salesOrder, node.transactionDateTime);
                    break;
                default:
                    break;
            }
            this.fillBlankDividerRow(financeTreeNodes, (index + 1).toString());
        });
        this.addTotalBalanceTreeNode(financeTreeNodes);
        return financeTreeNodes;
    }

    private getGroupProductSalesOrder(orders: OrderDataModel[], ledgers: OrderLedgersModel[])
        : { groupedProduct: any; salesOrder: OrderDataModel[]; individualOrders: OrderDataModel[]; productOrders: OrderDataModel[]} {
        const ledgerOrderIds = ledgers?.map(item => item.orderId);
        const salesOrders = orders.filter(item => ledgerOrderIds?.includes(item.orderId));
        
        const parentOrderSalesIds = salesOrders.map(item => item.parentOrderId);
        const individualOrders = orders.filter(item => parentOrderSalesIds.includes(item.orderId) && item.orderState?.code == OrderStateCode.Individual);
        const parentIndividualOrderIds = [...new Set(individualOrders.map(item => item.parentOrderId))];
        const productOrders = orders.filter(item => item.orderState?.code == OrderStateCode.Product && (parentIndividualOrderIds.includes(item.orderId) || parentOrderSalesIds.includes(item.orderId)));

        let groupedProducts = this.groupedProductSalesOrder(orders, ledgers, salesOrders, individualOrders, productOrders) 

        return {
            groupedProduct: groupedProducts,
            salesOrder: salesOrders,
            individualOrders: individualOrders,
            productOrders: productOrders
        };
    }

    private groupedProductSalesOrder(orders: OrderDataModel[], ledgers: OrderLedgersModel[], salesOrders: OrderDataModel[], individualOrders: OrderDataModel[], productOrders: OrderDataModel[]): any[] {
        let groupProduct = new Array();
        productOrders?.forEach(productOrder => {
            let refProduct = orders?.find(item => item.orderId == productOrder.referenceOrderId);
            let productNode = {
                code: productOrder?.orderState?.code,
                name: this.getProductDescription(refProduct.orderProduct?.[0]),
                id: productOrder?.orderId,
                transactionDateTime: null,
                orderDateTime: productOrder?.order?.dateTime,
                serviceDateTime: this.getProductServiceDateTime(productOrder, orders),
                children: null
            };
            let childrenSales = this.getProductChildSalesOrder(orders, ledgers, salesOrders, individualOrders, productOrder);
            productNode.transactionDateTime = childrenSales?.[0]?.transactionDateTime;
            productNode.children = childrenSales;
            groupProduct.push(productNode);
        })
        return groupProduct;
    }

    private getProductChildSalesOrder(orders: OrderDataModel[], ledgers: OrderLedgersModel[], salesOrders: OrderDataModel[], individualOrders: OrderDataModel[], productOrder: OrderDataModel): any[] {
        const productIndividualOrders = individualOrders?.filter(item => item?.orderState?.code == OrderStateCode.Individual && item.parentOrderId == productOrder?.orderId);
        const individualOrderIds = productIndividualOrders?.map(item => item.orderId);
        const productSalesOrders = salesOrders?.filter(item => individualOrderIds.includes(item.parentOrderId) || item.parentOrderId == productOrder.orderId);

        let displayTreeNodes = new Array();
        productSalesOrders.forEach(order => {
            const ledger = ledgers.find(item => item.orderId == order.orderId);
            displayTreeNodes.push({
                code: order?.orderState?.code,
                id: order?.orderId,
                transactionDateTime: ledger?.transactionDateTime,
                orderDateTime: order?.order?.dateTime,
                serviceDateTime: this.getProductServiceDateTime(order, orders)
            });
        })
        return this.sortPaymentSalesOrderAndPaymentDisplay(displayTreeNodes);
    }

    private sortPaymentSalesOrderAndPaymentDisplay(nodes: any[]): any[] {
        let sortNodes = nodes.sort((a, b) => {
            const aTransactionDateTime = this.getDateNumberForCompare(a?.transactionDateTime);
            const bTransactionDateTime = this.getDateNumberForCompare(b?.transactionDateTime);
            const aOrderDateTime = this.getDateNumberForCompare(a?.orderDateTime);
            const bOrderDateTime = this.getDateNumberForCompare(b?.orderDateTime);
            const aServiceDateTime = this.getDateNumberForCompare(a?.serviceDateTime);
            const bServiceDateTime = this.getDateNumberForCompare(b?.serviceDateTime);
            if (aTransactionDateTime == bTransactionDateTime) {
                return aOrderDateTime - bOrderDateTime;
            } else if (aOrderDateTime == bOrderDateTime) {
                return aServiceDateTime - bServiceDateTime;
            } else if (aServiceDateTime == bServiceDateTime) {
                return a.name?.localeCompare(b.name);
            }
            return aTransactionDateTime - bTransactionDateTime;
        })
        return sortNodes;
    }

    private getPaymentNode(payments: OrderPaymentModel[], paymentId: string): any[] {
        let displayTreeNodes = new Array();
        payments = payments.filter(payment => payment.paymentId == paymentId || paymentId == null);
        payments?.forEach(payment => {
            let paymentDateTime = payment?.void?.dateTime ?? payment?.issue?.dateTime ?? payment?.payment?.dateTime ?? payment?.commit?.dateTime;
            displayTreeNodes.push({
                code: this.paymentCode,
                id: payment?.paymentId,
                transactionDateTime: paymentDateTime,
                orderDateTime: paymentDateTime,
                serviceDateTime: paymentDateTime
            });
        })
        return displayTreeNodes;
    }

    private getSortedPaymentOrderTreeNodes(productNodes: any[], paymentNodes: any[], ledgers: OrderLedgersModel[]): any[] {
        productNodes = this.sortPaymentOrderAndPaymentDisplay(productNodes, ledgers)
        return [...productNodes, ...paymentNodes]
    }

    private sortPaymentOrderAndPaymentDisplay(nodes: any[], ledgers: OrderLedgersModel[]): any[] {
        let sortNodes = this.sortPaymentSalesOrderAndPaymentDisplay(nodes);
        sortNodes = this.fillNoneSalesTreeNode(sortNodes, OrderStateCode.Exchange);
        sortNodes = this.fillNoneSalesTreeNode(sortNodes, OrderStateCode.Refund);
        return this.sortPaymentSalesOrderAndPaymentDisplay(sortNodes);
    }

    private fillNoneSalesTreeNode(nodes: any[], orderStateCode: string): any[] {
        let productNodes = cloneDeep(nodes);
        let addedItem = 0;
        productNodes?.forEach((node, index) => {
            let nodesIndex = index + addedItem;
            let stateNodes = node?.children?.filter(item => item.code == orderStateCode);
            if (stateNodes?.length > 0 && stateNodes?.length == node?.children?.length) {
                nodes[nodesIndex].code = orderStateCode;
            } else if (stateNodes?.length > 0) {
                nodes[nodesIndex].children = node?.children?.filter(item => item.code != orderStateCode);
                let stateProduct = cloneDeep(node);
                stateProduct.code = orderStateCode;
                stateProduct.children = cloneDeep(stateNodes);
                stateProduct.transactionDateTime = stateNodes[0]?.transactionDateTime;
                stateProduct.orderDateTime = stateNodes[0]?.orderDateTime;
                nodes.splice(nodesIndex + 1, 0, stateProduct);
                addedItem += 1;
            }
        })
        return nodes;
    }

    private fillProductForPaymentTreeNode(financeTreeNodes: OrderDetailFinanceAccountingView[],
        sortedNode: any,
        orders: OrderDataModel[],
        saleOrders: OrderDataModel[],
        individualOrders: OrderDataModel[],
        ledgers: OrderLedgersModel[],
        refundFlag: boolean = false) {

        const productOrder = orders.find(order => order.orderId == sortedNode?.id);
        const refProductOrder = orders.find(order => order.orderId == productOrder?.referenceOrderId);
        const orderProduct = refProductOrder?.orderProduct?.[0];
        if (orderProduct?.productCategory?.code == ProductCategoryConstant.Voucher) {
            const voucherSalesOrder = saleOrders?.find(order => order.parentOrderId == productOrder?.orderId);
            this.fillProductOrderTreeNode(financeTreeNodes, voucherSalesOrder?.orderId, orders, saleOrders, ledgers, orderProduct, refundFlag);
            return;
        }

        const individualOrder = individualOrders.find(order => order.parentOrderId == productOrder?.orderId);
        const saleOrder = saleOrders?.find(order => order.parentOrderId == individualOrder?.orderId);
        const saleExchangeLedger = ledgers?.find(ledger => ledger.orderId == saleOrder?.orderId && ledger?.exchangeLedgerId);
        const saleLedger = ledgers?.find(ledger => ledger.orderId == saleOrder?.orderId && ledger?.exchangeLedgerId == null);
        let hierarchyId = sortedNode?.id;
        let productNodeIndex = financeTreeNodes.findIndex(node => node.hierarchyId == hierarchyId);
        if (productNodeIndex >= 0) hierarchyId = `${hierarchyId}_${productNodeIndex + 1}`;

        this.checkExchangeProductForPaymentTree(financeTreeNodes, saleOrder, saleExchangeLedger);

        financeTreeNodes.push({
            dateTime: !saleExchangeLedger?.exchangeLedgerId ? this.dateConversion.formatSystemDateTime(saleLedger?.transactionDateTime ?? saleOrder?.order?.dateTime) : null,
            description: this.getProductDescription(orderProduct),
            numberOfUnits: null,
            percent: null,
            amount: null,
            debitAmount: null,
            creditAmount: null,
            balanceAmount: null,
            hierarchyId: hierarchyId,
            parentHierarchyId: null,
            referenceOrderId: productOrder?.referenceOrderId,
            icon: this.getSalesProductIcon(saleExchangeLedger, orderProduct, false),
            iconClass: saleExchangeLedger?.exchangeLedgerId ? this.iconClassRed : null,
            type: orderProduct?.productCategory?.code == ProductCategoryConstant.Voucher ? FinanceAccountionType.Voucher : FinanceAccountionType.Product
        })
        this.fillProductChildrenForPaymentTreeNode(financeTreeNodes,
            sortedNode,
            hierarchyId,
            orders,
            saleOrders,
            ledgers,
            refundFlag
        );
    }

    private fillProductChildrenForPaymentTreeNode(financeTreeNodes: OrderDetailFinanceAccountingView[],
        sortedNode: any,
        hierarchyId: string,
        orders: OrderDataModel[],
        saleOrders: OrderDataModel[],
        ledgers: OrderLedgersModel[],
        refundFlag: boolean = false) {
        
        sortedNode?.children?.forEach(child => {
            this.fillSalesForPaymentTreeNode(financeTreeNodes,
                child?.id,
                hierarchyId,
                orders,
                saleOrders,
                ledgers,
                refundFlag);
        })
    }

    private fillSalesForPaymentTreeNode(financeTreeNodes: OrderDetailFinanceAccountingView[],
        orderId: string,
        parentHierarchyId: string,
        orders: OrderDataModel[],
        saleOrders: OrderDataModel[],
        ledgers: OrderLedgersModel[],
        refundFlag: boolean = false) {
        
        const saleOrder = saleOrders?.find(order => order.orderId == orderId);
        const individualOrder = orders.find(order => order.orderId == saleOrder.parentOrderId);
        const refIndividualOrder = orders.find(order => order.orderId == individualOrder.referenceOrderId);
        const orderPartner = refIndividualOrder?.orderPartner?.[0];
        const salesLedgerTransactionAmount = ledgers?.find(ledger => ledger.orderId == saleOrder.orderId)?.transaction?.amount;
        const nodesWithBalance = financeTreeNodes.filter(node => node.balanceAmount !== null);
        const lastestNode = nodesWithBalance[nodesWithBalance.length - 1];
        const saleExchangeLedger = ledgers?.find(ledger => ledger.orderId == saleOrder.orderId && ledger.exchangeLedgerId);
        const creditAmountFlag = refundFlag ? refundFlag : saleExchangeLedger?.ledgerTransaction?.code == PaymentConstant.cr;
        
        financeTreeNodes.push({
            dateTime: null,
            description: this.getIndividualDesc(orderPartner),
            numberOfUnits: individualOrder.numberOfUnits,
            percent: null,
            amount: null,
            debitAmount: !creditAmountFlag ? salesLedgerTransactionAmount : null,
            creditAmount: creditAmountFlag ? salesLedgerTransactionAmount : null,
            balanceAmount: creditAmountFlag ? (lastestNode?.balanceAmount ?? 0) + salesLedgerTransactionAmount : (lastestNode?.balanceAmount ?? 0) - salesLedgerTransactionAmount,
            hierarchyId: orderId,
            parentHierarchyId: parentHierarchyId,
            referenceOrderId: saleOrder?.referenceOrderId,
            type: FinanceAccountionType.Individual,
            individualIconClass: this.getPassengerClassName(orderPartner)
        })
        const childHeirarchyId = `${this.prefixPriceDetailId}${orderId}`;
        this.fillPriceDetailNode(financeTreeNodes, orderId, childHeirarchyId);
        this.fillProductOrderAmountDetail(financeTreeNodes, saleOrder, childHeirarchyId);
    }

    private checkExchangeProductForPaymentTree(financeTreeNodes: OrderDetailFinanceAccountingView[], saleOrder: OrderDataModel, saleLedger: OrderLedgersModel) {
        if (!saleLedger?.exchangeLedgerId) return;

        financeTreeNodes.push({
            dateTime: this.dateConversion.formatSystemDateTime(saleLedger.transactionDateTime),
            description: this.exchangeDebitDespription,
            numberOfUnits: null,
            percent: null,
            amount: null,
            debitAmount: null,
            creditAmount: null,
            balanceAmount: null,
            hierarchyId: `${this.prefixExchangeId}${saleOrder.orderId}`,
            parentHierarchyId: null,
            icon: this.exchangeIcon,
            type: FinanceAccountionType.Exchange
        })
    }

    private fillExchangeProductForPaymentTree(financeTreeNodes: OrderDetailFinanceAccountingView[], productNode: any, saleOrders: OrderDataModel[], ledgers: OrderLedgersModel[]) {
        let exchangeNode = productNode?.children?.find(item => item.code == OrderStateCode.Exchange);
        if (!exchangeNode) return;
        let exchange = saleOrders.find(order => order.orderId == exchangeNode?.id);
        let ledger = ledgers.find(ledger => ledger.orderId == exchange?.orderId);
        let exchangeCreditSales = saleOrders.find(order => order.orderState?.code == OrderStateCode.Sales && order.parentOrderId == exchange.parentOrderId);

        financeTreeNodes.push({
            dateTime: this.dateConversion.formatSystemDateTime(exchange.order?.dateTime),
            description: this.exchangeCreditDespription,
            numberOfUnits: null,
            percent: null,
            amount: null,
            debitAmount: null,
            creditAmount: null,
            balanceAmount: null,
            hierarchyId: `${this.prefixExchangeId}${exchange?.orderId}`,
            parentHierarchyId: null,
            icon: this.exchangeIcon,
            type: FinanceAccountionType.Exchange
        })
        this.fillExchangeCreditForPaymentTreeNode(financeTreeNodes, productNode?.id, ledger, exchangeCreditSales);
    }

    private fillExchangeCreditForPaymentTreeNode(financeTreeNodes: OrderDetailFinanceAccountingView[], productNodeId: string, ledger: OrderLedgersModel, saleOrder: OrderDataModel) {
        const nodesWithBalance = financeTreeNodes.filter(node => node.balanceAmount !== null);
        const lastestNode = nodesWithBalance[nodesWithBalance.length - 1];
        const exchangeCreditId = `${productNodeId}_${ledger.ledgerId}`
        const ledgerAmount = ledger.transaction?.amount ?? 0;

        const productNode = cloneDeep(financeTreeNodes.find(node => node.hierarchyId == productNodeId));
        if (!productNode) return;
        productNode.hierarchyId = `${this.prefixExchangeId}${productNodeId}`
        productNode.dateTime = null;
        productNode.icon = this.deleteIcon;
        productNode.iconClass = this.iconClassRed;
        financeTreeNodes.push(productNode);
        let individualNodes = cloneDeep(financeTreeNodes.filter(node => node.parentHierarchyId == productNodeId));
        individualNodes.forEach(individualNode => {
            individualNode.hierarchyId = exchangeCreditId;
            individualNode.parentHierarchyId = productNode.hierarchyId;
            individualNode.debitAmount = null;
            individualNode.creditAmount = ledgerAmount;
            individualNode.balanceAmount = (lastestNode?.balanceAmount ?? 0) + ledgerAmount;
            financeTreeNodes.push(individualNode)

            const priceDetailId = `${this.prefixPriceDetailId}${exchangeCreditId}`
            this.fillPriceDetailNode(financeTreeNodes, exchangeCreditId, priceDetailId);
            this.fillProductOrderAmountDetail(financeTreeNodes, saleOrder, priceDetailId, true);
        })
        
    }

    private fillPaymentForPaymentTree(financeTreeNodes: OrderDetailFinanceAccountingView[],
        paymentId: string,
        payments: OrderPaymentModel[],
        saleOrders: OrderDataModel[],
        dateTime: Date) {

        const payment = payments.find(payment => payment.paymentId == paymentId);
        const paymentDesc = `${payment?.formOfPayment?.name} ${payment?.document?.numberDisplay ?? payment?.payment?.numberDisplay ?? ''}`
        const saleOrderIds = saleOrders?.map(order => order.orderId);
        const individualLedgers = payment?.ledger?.filter(ledger => saleOrderIds.includes(ledger.orderId) && !ledger.exchangeLedgerId && ledger.exchangeFlag !== true);
        const nodesWithBalance = financeTreeNodes.filter(node => node.balanceAmount !== null);
        const lastestNode = nodesWithBalance[nodesWithBalance.length - 1];
        const paymentLedger = payment?.ledger?.find(ledger => ledger.paymentId);
        const sumOfLedger = individualLedgers?.reduce((sum, ledger) => sum + ledger.transaction?.amount, 0);
        const creditPaymentFlag = paymentLedger.ledgerTransaction?.code == PaymentConstant.cr;

        financeTreeNodes.push({
            dateTime: this.dateConversion.formatSystemDateTime(dateTime),
            description: paymentDesc,
            numberOfUnits: null,
            percent: null,
            amount: paymentLedger.transaction?.amount,
            debitAmount: !creditPaymentFlag ? sumOfLedger : null,
            creditAmount: creditPaymentFlag ? sumOfLedger : null,
            balanceAmount: creditPaymentFlag ? (lastestNode?.balanceAmount ?? 0) + sumOfLedger : (lastestNode?.balanceAmount ?? 0) - sumOfLedger,
            hierarchyId: paymentId,
            parentHierarchyId: null,
            type: creditPaymentFlag ? FinanceAccountionType.Payment : FinanceAccountionType.Refund
        })
        this.checkCancelledPayment(financeTreeNodes, payment, sumOfLedger, creditPaymentFlag);
    }

    private checkCancelledPayment(financeTreeNodes: OrderDetailFinanceAccountingView[], payment: OrderPaymentModel, sumOfLedger: number, creditPaymentFlag: boolean = true) {
        if (payment?.paymentStatus.code == PaymentConstant.cancelled) {
            let paymentNode = cloneDeep(financeTreeNodes.find(item => item.hierarchyId == payment?.paymentId));
            if (!paymentNode) return;
            const hierarchyId = `${this.prefixCancelId}${payment?.paymentId}`;
            const nodesWithBalance = financeTreeNodes.filter(node => node.balanceAmount !== null);
            const lastestNode = nodesWithBalance[nodesWithBalance.length - 1];

            paymentNode.hierarchyId = `${this.prefixCancelId}${payment?.paymentId}`;
            paymentNode.dateTime = this.dateConversion.formatSystemDateTime(payment.void?.dateTime ?? payment.commit?.dateTime);
            paymentNode.debitAmount = creditPaymentFlag ? sumOfLedger : null;
            paymentNode.creditAmount = !creditPaymentFlag ? sumOfLedger : null;
            paymentNode.balanceAmount = creditPaymentFlag ? (lastestNode?.balanceAmount ?? 0) + sumOfLedger : (lastestNode?.balanceAmount ?? 0) - sumOfLedger;
            paymentNode.icon = this.deleteIcon;
            paymentNode.iconClass = this.iconClassRed;
            paymentNode.type = FinanceAccountionType.CancelledPayment

            this.fillBlankDividerRow(financeTreeNodes, `${this.prefixBlankDividerId}${hierarchyId}`);
            financeTreeNodes.push(paymentNode);
        }
    }

    public getPaymentHeaderAccounting(orderJson: OrderJsonModel): OrderDetailFinanceAccountingView[] {
        if (!orderJson) return;
        const orders = orderJson?.oopsOrder?.order;
        const payments = orderJson?.oopsOrder?.payment;
        const ledgers = payments?.flatMap(paymant => paymant.ledger?.filter(item => item.parentLedgerId != null));

        const salesOrders = orders?.filter(order => order.orderState?.code == OrderStateCode.Sales || order.orderState?.code == OrderStateCode.Refund || order.orderState?.code == OrderStateCode.Exchange);
        const salesOrderParentIds = salesOrders?.map(order => order.parentOrderId);
        const individualOrders = orders?.filter(order => salesOrderParentIds?.includes(order.orderId));
        const individualOrderParentIds = individualOrders?.map(order => order.parentOrderId);
        const productItemOrders = orders?.filter(order => order.orderState?.code == OrderStateCode.Product && (individualOrderParentIds.includes(order.orderId) || salesOrderParentIds.includes(order.orderId)));

        let financeTreeNodes = new Array<OrderDetailFinanceAccountingView>();
        let groupedProduct = this.groupedProductSalesOrderForHeader(orders, salesOrders, individualOrders, productItemOrders);
        let paymentNodes = this.getPaymentNodeForHeader(payments, null);
        let sortedTreeNodeDisplay = this.sortPaymentOrderAndPaymentHeaderDisplay([...groupedProduct, ...paymentNodes], ledgers);
        sortedTreeNodeDisplay?.forEach((node, index) => {
            switch (node?.code) {
                case OrderStateCode.Refund:
                    this.fillProductForHeader(financeTreeNodes, node, orders, salesOrders, individualOrders, ledgers, true);
                    break;
                case OrderStateCode.Exchange:
                    this.fillExchangeTreeNodeForHeader(financeTreeNodes, node, orders, ledgers)
                    break;
                case OrderStateCode.Product:
                    this.fillProductForHeader(financeTreeNodes, node, orders, salesOrders, individualOrders, ledgers);
                    break;
                case this.paymentCode:
                    this.fillPaymentTreeNodeForHeader(financeTreeNodes, node.id, payments, salesOrders, node.orderDateTime, orders);
                    break;
                case OrderSalesStatusCode.Cancel:
                    this.fillCancelProductHeader(financeTreeNodes, node, orders, salesOrders);
                    break;
                default:
                    break;
            }
            this.fillBlankDividerRow(financeTreeNodes, (index + 1).toString());
        });
        this.addTotalBalanceTreeNode(financeTreeNodes);
        return financeTreeNodes;
    }

    private getPaymentNodeForHeader(payments: OrderPaymentModel[], paymentId: string): any[] {
        let displayTreeNodes = new Array();
        payments = payments?.filter(payment => (payment.paymentStatus.code == PaymentConstant.paid || payment.paymentStatus.code == PaymentConstant.issued) && (payment.paymentId == paymentId || paymentId == null));
        payments?.forEach(payment => {
            let paymentDateTime = payment?.void?.dateTime ?? payment?.issue?.dateTime ?? payment?.payment?.dateTime ?? payment?.commit?.dateTime;
            displayTreeNodes.push({
                code: this.paymentCode,
                id: payment?.paymentId,
                orderDateTime: paymentDateTime,
                serviceDateTime: paymentDateTime
            });
        })
        return displayTreeNodes;
    }

    private groupedProductSalesOrderForHeader(orders: OrderDataModel[], salesOrders: OrderDataModel[], individualOrders: OrderDataModel[], productOrders: OrderDataModel[]): any[] {
        let groupProduct = new Array();
        productOrders?.forEach(productOrder => {
            let productRef = orders?.find(order => order.orderId == productOrder?.referenceOrderId);
            let productNode = {
                code: productOrder?.orderState?.code,
                id: productOrder?.orderId,
                orderDateTime: productOrder?.order?.dateTime,
                serviceDateTime: this.getFlightEarlyServiceDateTime(productRef?.orderProduct?.[0]),
                children: null
            };
            let childrenSales = this.getProductChildSalesNodesForHeader(orders, salesOrders, individualOrders, productOrder);
            let groupedByOrderDateTime = this.getGroupedProductSalesByOrderDateTime(productNode, childrenSales);
            groupProduct.push(...groupedByOrderDateTime);
            this.groupedProductSalesCancelForHeader(groupProduct, orders, salesOrders, individualOrders, productOrder, cloneDeep(groupedByOrderDateTime));
        })
        return groupProduct;
    }

    private getGroupedProductSalesByOrderDateTime(productNode: any, childrenSales: any[]): any[] {
        let groupedSales = this.groupProductSalesByOrderDateTime(childrenSales);
        let keys = Object.keys(groupedSales);
        let groupProduct = new Array();
        keys?.forEach(key => {
            let children = groupedSales[key];
            if (children?.length <= 0) return;
            let newNode = cloneDeep(productNode);
            newNode.orderDateTime = new Date(key);
            newNode.children = groupedSales[key];
            groupProduct.push(newNode);
        })
        return groupProduct;
    }

    private groupProductSalesByOrderDateTime(childrenSales: any[]) {
        let groupedSales = childrenSales.reduce((list, currentItem) => {
            const key = this.dateConversion.formatSystemDateTime(currentItem['orderDateTime']);
            if (!list[key]) {
                list[key] = [];
            }
            list[key].push(currentItem);
            return list;
        }, {});
        return groupedSales;
    }

    private groupedProductSalesCancelForHeader(groupProduct: any[], orders: OrderDataModel[], salesOrders: OrderDataModel[], individualOrders: OrderDataModel[], productOrder: OrderDataModel, productNodes: any[]) {
        let childrenSalesOrder = this.getProductChildSalesOrderForHeader(salesOrders, individualOrders, productOrder);
        let childrenIndividualOrders = individualOrders.filter(individualOrder => childrenSalesOrder.find(salesOrder => salesOrder.parentOrderId == individualOrder.orderId));
        let cancelledIndividualOrders = childrenIndividualOrders?.filter(order => order.orderSalesStatus?.code == OrderSalesStatusCode.Cancel);
        let cancelSalesOrders = childrenSalesOrder?.filter(salesOrder => cancelledIndividualOrders.find(individualOrder => individualOrder.orderId == salesOrder.parentOrderId));
        
        if (cancelSalesOrders?.length <= 0 || childrenSalesOrder.find(order => order.orderState.code == OrderStateCode.Refund || order.orderState.code == OrderStateCode.Exchange)) return;
        productNodes?.forEach(productNode => {
            let cancelledNode = cloneDeep(productNode);
            let productNodeChildrenOrders = cancelSalesOrders.filter(order => cancelledNode?.children?.find(node => node.id == order.orderId));
            if (productNodeChildrenOrders?.length <= 0 ) return;
            
            const cancelTotalAmount = productNodeChildrenOrders?.[0]
                ?.orderAmount?.find(amount => amount.orderAmountType.code == OrderAmountTypeCode.Total);
            if (!cancelTotalAmount?.cancel?.dateTime) return;
            let cancelDateTime = this.dateConversion.formatSystemDateTime(cancelTotalAmount?.cancel?.dateTime)
            cancelledNode.code = OrderSalesStatusCode.Cancel;
            cancelledNode.orderDateTime = new Date(cancelDateTime);
            cancelledNode.children = this.mapProductChildSalesOrdersToNodes(orders, productNodeChildrenOrders);
            groupProduct.push(cancelledNode);
        })
    }

    private getProductChildSalesNodesForHeader(orders: OrderDataModel[], salesOrders: OrderDataModel[], individualOrders: OrderDataModel[], productOrder: OrderDataModel): any[] {
        const productSalesOrders = this.getProductChildSalesOrderForHeader(salesOrders, individualOrders, productOrder);
        return this.mapProductChildSalesOrdersToNodes(orders, productSalesOrders);
    }

    private mapProductChildSalesOrdersToNodes(orders: OrderDataModel[], productSalesOrders: OrderDataModel[]): any[] {
        let displayTreeNodes = new Array();
        productSalesOrders.forEach(order => {
            displayTreeNodes.push({
                code: order?.orderState?.code,
                id: order?.orderId,
                orderDateTime: order?.order?.dateTime,
                serviceDateTime: this.getProductServiceDateTime(order, orders)
            });
        })
        return this.sortPaymentSalesOrderAndPaymentDisplay(displayTreeNodes);
    }

    private getProductChildSalesOrderForHeader(salesOrders: OrderDataModel[], individualOrders: OrderDataModel[], productOrder: OrderDataModel): OrderDataModel[] {
        let productIndividualOrders = individualOrders?.filter(item => item?.orderState?.code == OrderStateCode.Individual 
            && item.parentOrderId == productOrder?.orderId);
        const individualOrderIds = productIndividualOrders?.map(item => item.orderId);
        const productSalesOrders = salesOrders?.filter(item => individualOrderIds.includes(item.parentOrderId) || item.parentOrderId == productOrder.orderId);

        return productSalesOrders;
    }

    private sortPaymentOrderAndPaymentHeaderDisplay(nodes: any[], ledgers: OrderLedgersModel[]): any[] {
        let sortNodes = this.fillHeaderNoneSalesTreeNode(nodes, OrderStateCode.Exchange);
        sortNodes = this.fillHeaderNoneSalesTreeNode(sortNodes, OrderStateCode.Refund);
        sortNodes = this.sortHeaderOrderAndPaymentDisplay(sortNodes);
        return this.sortExchangeNodeForHeader(sortNodes, ledgers);
    }

    private sortExchangeNodeForHeader(nodes: any[], ledgers: OrderLedgersModel[]): any[] {
        let exchangeNodeIds = nodes?.filter(node => node.code == OrderStateCode.Exchange).map(node => node?.children?.[0]?.id);
        if (exchangeNodeIds?.length <= 0) return nodes;
        let exchanges = ledgers?.filter(ledger => exchangeNodeIds.includes(ledger?.orderId));
        exchanges?.forEach(exchange => {
            let salesExchange = ledgers?.find(ledger => ledger.exchangeLedgerId == exchange?.ledgerId);
            let salesExchangeIndex = nodes?.findIndex(node => node?.children?.[0]?.id == salesExchange?.orderId);
            let salesExchangeNode = nodes?.splice(salesExchangeIndex, 1);

            let exchangeIndex = nodes.findIndex(node =>  node?.children?.[0]?.id == exchange?.orderId);
            nodes.splice(exchangeIndex + 1, 0, ...salesExchangeNode);
        })
        return nodes;
    }

    private sortHeaderOrderAndPaymentDisplay(nodes: any[]): any[] {
        let sortNodes = nodes.sort((a, b) => {
            const aOrderDateTime = this.getDateNumberForCompare(a?.orderDateTime);
            const bOrderDateTime = this.getDateNumberForCompare(b?.orderDateTime);
            const aServiceDateTime = this.getDateNumberForCompare(a?.serviceDateTime);
            const bServiceDateTime = this.getDateNumberForCompare(b?.serviceDateTime);
            if (aOrderDateTime == bOrderDateTime) {
                return aServiceDateTime - bServiceDateTime;
            }
            return aOrderDateTime - bOrderDateTime;
        })
        return sortNodes;
    }

    private fillHeaderNoneSalesTreeNode(nodes: any[], orderStateCode: string): any[] {
        let productNodes = cloneDeep(nodes);
        productNodes?.forEach((node, index) => {
            if (node?.code != OrderStateCode.Product) return;
            let stateNodes = node?.children?.filter(item => item.code == orderStateCode);
            if (stateNodes?.length > 0 && stateNodes?.length == node?.children?.length) {
                nodes[index].code = orderStateCode;
                nodes[index].orderDateTime = stateNodes?.[0]?.orderDateTime;
                nodes[index].id = `${orderStateCode}_${nodes[index].id}`
            } else if (stateNodes?.length > 0) {
                nodes[index].children = node?.children?.filter(item => item.code != orderStateCode);
                let stateProduct = cloneDeep(node);
                stateProduct.orderDateTime = stateNodes?.[0]?.orderDateTime;
                stateProduct.code = orderStateCode;
                stateProduct.children = stateNodes;
                stateProduct.id = `${orderStateCode}_${stateProduct.id}`
                nodes.splice(index + 1, 0, stateProduct);
            }    
        })
        return nodes;
    }

    private fillProductForHeader(financeTreeNodes: OrderDetailFinanceAccountingView[],
        sortedNode: any,
        orders: OrderDataModel[],
        saleOrders: OrderDataModel[],
        individualOrders: OrderDataModel[],
        ledgers: OrderLedgersModel[],
        refundFlag: boolean = false) {

        const productOrderId = refundFlag ? sortedNode?.id?.split('_')?.[1] : sortedNode?.id;
        const productOrder = orders.find(order => order.orderId == productOrderId);
        const refProductOrder = orders.find(order => order.orderId == productOrder?.referenceOrderId);
        const orderProduct = refProductOrder?.orderProduct?.[0];
        if (orderProduct?.productCategory?.code == ProductCategoryConstant.Voucher) {
            const voucherSalesOrder = saleOrders?.find(order => order.parentOrderId == productOrder?.orderId);
            this.fillProductOrderTreeNode(financeTreeNodes, voucherSalesOrder?.orderId, orders, saleOrders, ledgers, orderProduct, refundFlag);
            return;
        }

        const individualOrder = individualOrders.find(order => order.parentOrderId == productOrder?.orderId);
        const saleOrder = saleOrders?.find(order => order.parentOrderId == individualOrder?.orderId && sortedNode?.children?.find(child => child.id == order.orderId));
        const saleExchangeLedger = ledgers?.find(ledger => ledger.orderId == saleOrder?.orderId && ledger?.exchangeLedgerId);
        let hierarchyId = sortedNode?.id;
        let productNodeIndex = financeTreeNodes.findIndex(node => node.hierarchyId == hierarchyId);
        if (productNodeIndex >= 0) hierarchyId = `${hierarchyId}_${productNodeIndex + 1}`;

        financeTreeNodes.push({
            dateTime: this.dateConversion.formatSystemDateTime(sortedNode?.orderDateTime),
            description: this.getProductDescription(orderProduct),
            numberOfUnits: null,
            percent: null,
            amount: null,
            debitAmount: null,
            creditAmount: null,
            balanceAmount: null,
            hierarchyId: hierarchyId,
            parentHierarchyId: null,
            referenceOrderId: productOrder?.referenceOrderId,
            icon: this.getSalesProductIcon(saleExchangeLedger, orderProduct, refundFlag),
            iconClass: saleExchangeLedger?.exchangeLedgerId || refundFlag ? this.iconClassRed : null,
            type: orderProduct?.productCategory?.code == ProductCategoryConstant.Voucher ? FinanceAccountionType.Voucher : FinanceAccountionType.Product
        })
        this.fillProductChildrenForHeader(financeTreeNodes,
            sortedNode,
            orders,
            productOrder,
            saleOrders,
            ledgers,
            hierarchyId,
            refundFlag
        );

        this.checkExchangeProductForHeader(financeTreeNodes, saleOrder, saleExchangeLedger);
    }

    private checkExchangeProductForHeader(financeTreeNodes: OrderDetailFinanceAccountingView[], saleOrder: OrderDataModel, saleLedger: OrderLedgersModel) {
        if (!saleLedger?.exchangeLedgerId) return;
        const nodesWithBalance = financeTreeNodes.filter(node => node.balanceAmount !== null);
        const lastestNode = nodesWithBalance[nodesWithBalance.length - 1];

        this.fillBlankDividerRow(financeTreeNodes, `${this.prefixBlankDividerId}${this.prefixExchangeId}${saleOrder.orderId}`)
        financeTreeNodes.push({
            dateTime: this.dateConversion.formatSystemDateTime(saleLedger.transactionDateTime),
            description: this.exchangeCreditDespription,
            numberOfUnits: null,
            percent: null,
            amount: null,
            debitAmount: null,
            creditAmount: saleLedger?.transaction?.amount,
            balanceAmount: (lastestNode.balanceAmount ?? 0) + saleLedger?.transaction?.amount,
            hierarchyId: `${this.prefixExchangeId}${saleOrder.orderId}`,
            parentHierarchyId: null,
            icon: this.exchangeIcon,
            type: FinanceAccountionType.Exchange
        })
    }

    private fillProductChildrenForHeader(financeTreeNodes: OrderDetailFinanceAccountingView[],
        sortedNode: any,
        orders: OrderDataModel[],
        productOrder: OrderDataModel,
        saleOrders: OrderDataModel[],
        ledgers: OrderLedgersModel[],
        parentHierarchyId: string,
        refundFlag: boolean = false) {
        if (refundFlag) {
            saleOrders = saleOrders?.filter(order => order.orderState?.code == OrderStateCode.Refund);
        } else {
            saleOrders = saleOrders?.filter(order => order.orderState?.code == OrderStateCode.Sales);
        }
        sortedNode?.children?.forEach(child => {
            this.fillSalesForHeader(financeTreeNodes,
                child?.id,
                parentHierarchyId,
                orders,
                productOrder,
                saleOrders,
                ledgers,
                refundFlag);
        })
    }

    private fillSalesForHeader(financeTreeNodes: OrderDetailFinanceAccountingView[],
        orderId: string,
        parentHierarchyId: string,
        orders: OrderDataModel[],
        productOrder: OrderDataModel,
        saleOrders: OrderDataModel[],
        ledgers: OrderLedgersModel[],
        refundFlag: boolean = false) {
        
        const saleOrder = saleOrders?.find(order => order.orderId == orderId);
        const individualOrder = orders.find(order => order.orderId == saleOrder.parentOrderId);
        const refIndividualOrder = orders.find(order => order.orderId == individualOrder.referenceOrderId);
        const orderPartner = refIndividualOrder?.orderPartner?.[0];
        const totalAmount = saleOrder?.orderAmount?.find(amount => amount.orderAmountType?.code == OrderAmountTypeCode.Total)?.sales?.amount ?? 0;
        const nodesWithBalance = financeTreeNodes.filter(node => node.balanceAmount !== null);
        const lastestNode = nodesWithBalance[nodesWithBalance.length - 1];
        const saleExchangeLedger = ledgers?.find(ledger => ledger.orderId == saleOrder.orderId && ledger.exchangeLedgerId);
        const creditAmountFlag = refundFlag ? refundFlag : saleExchangeLedger?.ledgerTransaction?.code == PaymentConstant.cr;
        const isAddLater = this.dateConversion.formatSystemDateTime(productOrder?.order?.dateTime) != this.dateConversion.formatSystemDateTime(saleOrder.order.dateTime);
        
        financeTreeNodes.push({
            dateTime: null,
            description: this.getIndividualDesc(orderPartner),
            numberOfUnits: individualOrder.numberOfUnits,
            percent: null,
            amount: null,
            debitAmount: !creditAmountFlag ? totalAmount : null,
            creditAmount: creditAmountFlag ? totalAmount : null,
            balanceAmount: creditAmountFlag ? (lastestNode?.balanceAmount ?? 0) + totalAmount : (lastestNode?.balanceAmount ?? 0) - totalAmount,
            hierarchyId: orderId,
            parentHierarchyId: parentHierarchyId,
            referenceOrderId: saleOrder?.referenceOrderId,
            type: FinanceAccountionType.Individual,
            individualIconClass: this.getPassengerClassName(orderPartner),
            icon: !refundFlag && isAddLater ? this.addShoppingCartIcon : null,
            iconClass: !refundFlag && isAddLater ? this.iconClassRed : null
        })
        const childHeirarchyId = `${this.prefixPriceDetailId}${orderId}`;
        this.fillPriceDetailNode(financeTreeNodes, orderId, childHeirarchyId);
        this.fillProductOrderAmountDetail(financeTreeNodes, saleOrder, childHeirarchyId);
    }

    private fillCancelProductHeader(financeTreeNodes: OrderDetailFinanceAccountingView[],
        sortedNode: any,
        orders: OrderDataModel[],
        saleOrders: OrderDataModel[]) {
        const productOrderId = sortedNode?.id;
        const productOrder = orders.find(order => order.orderId == productOrderId);
        this.checkCancelProducForHeader(financeTreeNodes, productOrder, saleOrders, orders);
    }

    private checkCancelProducForHeader(financeTreeNodes: OrderDetailFinanceAccountingView[], productOrder: OrderDataModel, saleOrders: OrderDataModel[], orders: OrderDataModel[]) {
        const productSalesOrders = financeTreeNodes.filter(node => node.parentHierarchyId == productOrder?.orderId)
            ?.map(node => saleOrders.find(order => order.orderId == node.hierarchyId));
        const totalAmounts = productSalesOrders.map(sales => sales.orderAmount?.find(amount => amount.orderAmountType?.code == OrderAmountTypeCode.Total))
        const cancelAmount = totalAmounts.find(total => total.cancel?.dateTime != null)
        if (!cancelAmount) return;
        this.fillCancelProductForHeader(financeTreeNodes, productOrder, orders, cancelAmount);
    }

    private fillCancelProductForHeader(financeTreeNodes: OrderDetailFinanceAccountingView[], productOrder: OrderDataModel, orders: OrderDataModel[], cancelAmount: OrderAmountModel) {
        const cancelProductNode = cloneDeep(financeTreeNodes?.find(node => node.hierarchyId == productOrder.orderId));
        const cancelProductHierarchyId = `${this.prefixCancelId}${cancelProductNode.hierarchyId}`;
        const isCancelledProduct = productOrder?.orderSalesStatus?.code == OrderSalesStatusCode.Cancel;
        cancelProductNode.dateTime = this.dateConversion.formatSystemDateTime(cancelAmount?.cancel?.dateTime);
        cancelProductNode.hierarchyId = cancelProductHierarchyId;
        cancelProductNode.icon = isCancelledProduct ? this.deleteIcon : cancelProductNode.icon;
        cancelProductNode.iconClass = isCancelledProduct ? this.iconClassRed : null;

        let cancelNodes = new Array<OrderDetailFinanceAccountingView>();
        cancelNodes.push(cancelProductNode)

        const cancelIndividualNodes = cloneDeep(financeTreeNodes?.filter(node => node.parentHierarchyId == productOrder.orderId));
        cancelIndividualNodes?.forEach(individualNode => {
            let salesOrder = orders.find(order => order.orderId == individualNode.hierarchyId);
            let individualOrder = orders.find(order => order.orderId == salesOrder.parentOrderId);
            let isCancelledIndividual = individualOrder?.orderSalesStatus?.code == OrderSalesStatusCode.Cancel;
            const nodesWithBalance = financeTreeNodes.filter(node => node.balanceAmount !== null);
            const lastestNode = nodesWithBalance[nodesWithBalance.length - 1];
            if (!isCancelledIndividual) return;
            const individualHierarchyId = `${this.prefixCancelId}${individualNode.hierarchyId}`;
            individualNode.icon = !isCancelledProduct && isCancelledIndividual ? this.deleteIcon : null;
            individualNode.iconClass = !isCancelledProduct && isCancelledIndividual ? this.iconClassRed : null;
            individualNode.parentHierarchyId = cancelProductHierarchyId;
            individualNode.hierarchyId = individualHierarchyId;
            individualNode.creditAmount = cloneDeep(individualNode.debitAmount);
            individualNode.debitAmount = null;
            individualNode.balanceAmount = (lastestNode.balanceAmount ?? 0) + (individualNode?.creditAmount ?? 0);
            cancelNodes.push(individualNode);

            this.fillCancelPriceDetailForHeader(financeTreeNodes, cancelNodes, individualHierarchyId, salesOrder, orders);
        })
        
        financeTreeNodes.push(...cancelNodes);
    }

    private fillCancelPriceDetailForHeader(financeTreeNodes: OrderDetailFinanceAccountingView[], cancelNodes: OrderDetailFinanceAccountingView[], parentHierarchyId: string, salesOrder: OrderDataModel, orders: OrderDataModel[]) {
        const priceDetaiHierarchyId = `${this.prefixPriceDetailId}${salesOrder.orderId}`;
        const priceDetaiNode = cloneDeep(financeTreeNodes?.find(node => node.hierarchyId == priceDetaiHierarchyId));
        const amountNodes = cloneDeep(financeTreeNodes?.filter(node => node.parentHierarchyId == priceDetaiNode.hierarchyId));
        const cancelParentHierarchyId = `${this.prefixCancelId}${priceDetaiHierarchyId}`
        priceDetaiNode.parentHierarchyId = parentHierarchyId;
        priceDetaiNode.hierarchyId = cancelParentHierarchyId;
        cancelNodes.push(priceDetaiNode);

        amountNodes.forEach(node => {
            node.amount = -node.amount
            node.parentHierarchyId = cancelParentHierarchyId
            node.hierarchyId = `${this.prefixCancelId}${node.hierarchyId}`
            cancelNodes.push(node)
        });
    }

    private fillPaymentTreeNodeForHeader(financeTreeNodes: OrderDetailFinanceAccountingView[],
        paymentId: string,
        payments: OrderPaymentModel[],
        saleOrders: OrderDataModel[],
        dateTime: Date,
        orders: OrderDataModel[]) {

        const payment = payments.find(payment => payment.paymentId == paymentId);
        const paymentDesc = `${payment?.formOfPayment?.name} ${payment?.document?.numberDisplay ?? payment?.payment?.numberDisplay}`
        const saleOrderIds = saleOrders?.map(order => order.orderId);
        const individualLedgers = payment?.ledger?.filter(ledger => saleOrderIds.includes(ledger.orderId) && !ledger.exchangeLedgerId && ledger.exchangeFlag !== true);
        const nodesWithBalance = financeTreeNodes.filter(node => node.balanceAmount !== null);
        const lastestNode = nodesWithBalance[nodesWithBalance.length - 1];
        const paymentLedger = payment?.ledger?.find(ledger => ledger.paymentId);
        const sumOfLedger = individualLedgers?.reduce((sum, ledger) => sum + ledger.transaction?.amount, 0);
        const creditPaymentFlag = paymentLedger.ledgerTransaction?.code == PaymentConstant.cr;
        const latestBalanceAmount = lastestNode?.type == FinanceAccountionType.Product 
            ? financeTreeNodes?.find(node => node.parentHierarchyId == lastestNode.hierarchyId)?.balanceAmount : lastestNode?.balanceAmount

        financeTreeNodes.push({
            dateTime: this.dateConversion.formatSystemDateTime(dateTime),
            description: paymentDesc,
            numberOfUnits: null,
            percent: null,
            amount: paymentLedger.transaction?.amount,
            debitAmount: !creditPaymentFlag ? sumOfLedger : null,
            creditAmount: creditPaymentFlag ? sumOfLedger : null,
            balanceAmount: creditPaymentFlag ? (latestBalanceAmount ?? 0) + sumOfLedger : (latestBalanceAmount ?? 0) - sumOfLedger,
            hierarchyId: paymentId,
            parentHierarchyId: null,
            type: creditPaymentFlag ? FinanceAccountionType.Payment : FinanceAccountionType.Refund
        })

        this.fillLedgerTreeNodeForHeader(financeTreeNodes, paymentId, individualLedgers, orders, saleOrders, !creditPaymentFlag);
    }

    private fillLedgerTreeNodeForHeader(financeTreeNodes: OrderDetailFinanceAccountingView[],
        paymentId: string,
        ledgers: OrderLedgersModel[],
        orders: OrderDataModel[],
        saleOrders: OrderDataModel[],
        refundFlag: boolean = false) {

        let ledgerOrderIds = ledgers?.map(ledger => ledger.orderId);
        saleOrders = saleOrders?.filter(order => ledgerOrderIds?.includes(order.orderId));
        let salesOrderParentIds = saleOrders?.map(order => order.parentOrderId);
        let individualOrders = orders.filter(order => salesOrderParentIds.includes(order.orderId));
        let individualOrdersParentIds = individualOrders?.map(order => order.parentOrderId);
        let productOrders = orders.filter(order => individualOrdersParentIds?.includes(order.orderId));
        const isNotTransaport = individualOrders?.[0]?.orderState?.code == OrderStateCode.Product;
        if (isNotTransaport) {
            this.fillNonTransportProductLedgerForHeader(financeTreeNodes, paymentId, ledgers, orders, individualOrders, refundFlag);
            return;
        }
        this.fillProductLedgerForHeader(financeTreeNodes, paymentId, ledgers, orders, productOrders, refundFlag)
    }

    private fillProductLedgerForHeader(financeTreeNodes: OrderDetailFinanceAccountingView[],
        paymentId: string,
        ledgers: OrderLedgersModel[],
        orders: OrderDataModel[],
        productOrders: OrderDataModel[],
        refundFlag: boolean) {
        productOrders?.forEach(productOrder => {
            const productTreeNode = financeTreeNodes?.find(node => node.hierarchyId == productOrder?.orderId);
            const paymentProductId = `${paymentId}_${productOrder?.orderId}`
            financeTreeNodes.push({
                dateTime: null,
                description: productTreeNode?.description,
                numberOfUnits: null,
                percent: null,
                amount: null,
                debitAmount: null,
                creditAmount: null,
                balanceAmount: null,
                hierarchyId: paymentProductId,
                parentHierarchyId: paymentId,
                icon: this.getLedgerProductGroupIcon(productTreeNode, orders),
                type: FinanceAccountionType.Product
            })
            this.fillIndividualLedgerForHeader(financeTreeNodes, paymentProductId, productOrder?.orderId, ledgers, refundFlag);
        }) 
    }

    private fillNonTransportProductLedgerForHeader(financeTreeNodes: OrderDetailFinanceAccountingView[],
        paymentId: string,
        ledgers: OrderLedgersModel[],
        orders: OrderDataModel[],
        productOrders: OrderDataModel[],
        refundFlag: boolean) {
            
        productOrders?.forEach(productOrder => {
            const productRef = orders?.find(order => order.orderId == productOrder?.referenceOrderId);
            const salesOrder = orders?.find(order => order.parentOrderId == productOrder?.orderId);
            const salesTreeNode = financeTreeNodes?.find(node => node.hierarchyId == salesOrder?.orderId);
            const productLedger = ledgers.find(ledger => ledger.orderId == salesOrder?.orderId);
            const paymentProductId = `${paymentId}_${productOrder?.orderId}`
            financeTreeNodes.push({
                dateTime: null,
                description: productRef?.orderProduct?.[0]?.orderProductName,
                numberOfUnits: null,
                percent: null,
                amount: productLedger?.transaction?.amount,
                debitAmount: null,
                creditAmount: null,
                balanceAmount: null,
                hierarchyId: paymentProductId,
                parentHierarchyId: paymentId,
                icon: salesTreeNode?.icon,
                type: FinanceAccountionType.Product
            })
            this.fillIndividualLedgerForHeader(financeTreeNodes, paymentProductId, productOrder?.orderId, ledgers, refundFlag);
        })
    }

    private fillIndividualLedgerForHeader(financeTreeNodes: OrderDetailFinanceAccountingView[],
        parentHierarchyId: string,
        productOrderId: string,
        ledgers: OrderLedgersModel[],
        refundFlag: boolean) {

        const productOrderHierarchyId = refundFlag ? `${OrderStateCode.Refund}_${productOrderId}` : productOrderId;
        const saleOrderIds = ledgers?.map(ledger => ledger.orderId);
        const salesTreeNodes = financeTreeNodes?.filter(node => saleOrderIds?.includes(node.hierarchyId) && node.parentHierarchyId == productOrderHierarchyId);
        salesTreeNodes?.forEach(node => {
            const ledger = ledgers?.find(item => item.orderId == node.hierarchyId);
            
            financeTreeNodes.push({
                dateTime: null,
                description: node?.description,
                numberOfUnits: null,
                percent: null,
                amount: ledger?.transaction?.amount,
                debitAmount: null,
                creditAmount: null,
                balanceAmount: null,
                hierarchyId: `${parentHierarchyId}_${node?.hierarchyId}`,
                parentHierarchyId: parentHierarchyId,
                individualIconClass: node.individualIconClass,
                type: FinanceAccountionType.Ledger
            })
        })
    }

    private fillExchangeTreeNodeForHeader(financeTreeNodes: OrderDetailFinanceAccountingView[], exchangeNode: any, orders: OrderDataModel[], ledgers: OrderLedgersModel[]) {
        const exhangeProductId =  exchangeNode?.id?.split('_')?.[1];
        let exchange = orders.find(order => order.orderId == exchangeNode?.children?.[0]?.id);
        let ledger = ledgers.find(ledger => ledger.orderId == exchange?.orderId);
        let exchangeCreditSales = orders.find(order => order.orderState?.code == OrderStateCode.Sales && order.parentOrderId == exchange.parentOrderId);

        this.fillExchangeCreditTreeNodeForHeader(financeTreeNodes, exhangeProductId, exchangeNode, orders);
        const ledgerAmount = ledger.transaction?.amount ?? 0;
        const rootTreeNodes = financeTreeNodes.filter(node => !node.parentHierarchyId && node.type != null);
        const lastestNode = rootTreeNodes[rootTreeNodes.length - 1];
        const latestBalanceAmount = lastestNode?.type == FinanceAccountionType.Product 
            ? financeTreeNodes?.find(node => node.parentHierarchyId == lastestNode.hierarchyId)?.balanceAmount : lastestNode?.balanceAmount

        this.fillBlankDividerRow(financeTreeNodes, `${this.prefixBlankDividerId}${exchangeCreditSales.orderId}`);
        financeTreeNodes.push({
            dateTime: this.dateConversion.formatSystemDateTime(exchange.order?.dateTime),
            description: this.exchangeDebitDespription,
            numberOfUnits: null,
            percent: null,
            amount: null,
            debitAmount: ledgerAmount,
            creditAmount: null,
            balanceAmount: (latestBalanceAmount ?? 0) - ledgerAmount,
            hierarchyId: exchangeNode?.id,
            parentHierarchyId: null,
            icon: this.exchangeIcon,
            type: FinanceAccountionType.Exchange
        })
    }

    private fillExchangeCreditTreeNodeForHeader(financeTreeNodes: OrderDetailFinanceAccountingView[], exchangeProductId: string, exchangeNode: any, orders: OrderDataModel[]) {
        const exchangeCreditId = `${OrderStateCode.Product}_${exchangeProductId}`

        const productNode = cloneDeep(financeTreeNodes.find(node => node.hierarchyId == exchangeProductId));
        if (!productNode) return;
        productNode.dateTime = this.dateConversion.formatSystemDateTime(exchangeNode?.children?.[0]?.orderDateTime);
        productNode.hierarchyId = exchangeCreditId;
        productNode.icon = this.deleteIcon;
        productNode.iconClass = this.iconClassRed;
        productNode.debitAmount = null;
        productNode.creditAmount = null;
        productNode.balanceAmount = null;
        financeTreeNodes.push(productNode)

        exchangeNode?.children?.forEach(child => {
            this.fillExchangeIndividualForHeader(financeTreeNodes, child.id, exchangeCreditId, orders)
        })
    }

    private fillExchangeIndividualForHeader(financeTreeNodes: OrderDetailFinanceAccountingView[], exchangeOrderId: string, exchangeProductOrderId: string, orders: OrderDataModel[]) {
        const exchangeOrder = orders?.find(order => order.orderId == exchangeOrderId);
        const individualOrder = orders.find(order => order.orderId == exchangeOrder.parentOrderId);
        const refIndividualOrder = orders.find(order => order.orderId == individualOrder.referenceOrderId);
        const orderPartner = refIndividualOrder?.orderPartner?.[0];
        const totalAmount = exchangeOrder?.orderAmount?.find(amount => amount.orderAmountType?.code == OrderAmountTypeCode.Total)?.sales?.amount ?? 0;
        const nodesWithBalance = financeTreeNodes.filter(node => node.balanceAmount !== null);
        const lastestNode = nodesWithBalance[nodesWithBalance.length - 1];
        
        financeTreeNodes.push({
            dateTime: null,
            description: this.getIndividualDesc(orderPartner),
            numberOfUnits: individualOrder.numberOfUnits,
            percent: null,
            amount: null,
            debitAmount: null,
            creditAmount: totalAmount,
            balanceAmount: (lastestNode?.balanceAmount ?? 0) + totalAmount,
            hierarchyId: exchangeOrderId,
            parentHierarchyId: exchangeProductOrderId,
            referenceOrderId: individualOrder.referenceOrderId,
            type: FinanceAccountionType.Individual,
            individualIconClass: this.getPassengerClassName(orderPartner)
        })
        const childHeirarchyId = `${this.prefixPriceDetailId}${exchangeOrderId}`;
        this.fillPriceDetailNode(financeTreeNodes, exchangeOrderId, childHeirarchyId);
        this.fillProductOrderAmountDetail(financeTreeNodes, exchangeOrder, childHeirarchyId);
    }
}
